import {
  AlertTriangle,
  BarChart3,
  Bell,
  BellOff,
  Box,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  KeyRound,
  Layers3,
  Lock,
  LogOut,
  Mail,
  MessageSquare,
  Pause,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Server,
  Shield,
  ShieldCheck,
  Siren,
  TerminalSquare,
  Trash2,
  UserCog,
  Users,
  XCircle
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createUser, deletePasskey, deletePushSubscription, deleteUser, downloadReport, fetchAuthOptions, fetchPasskeys, fetchPushStatus, fetchSecurityEvidence, fetchSession, fetchSnapshot, fetchUsers, generateReport, login, logout, passkeyAuthenticationOptions, passkeyRegistrationOptions, resetService, savePushSubscription, sendIncidentToSlack, sendMonthlyReportEmail, setSlackAlertsEnabled, updateAccount, updateUser, verifyPasskeyAuthentication, verifyPasskeyRegistration } from "./api";
import { createPasskeyAttestation, getPasskeyAssertion, getPasskeyErrorMessage, getPasskeySupport } from "./passkeys";
import { getPushSupport, subscribeToPush, unsubscribeFromPush } from "./push";
import type {
  AccessOptions,
  CertificateRow,
  EndpointProbeRow,
  EnvironmentSummary,
  Incident,
  LogEventRow,
  PortalPasskey,
  PushSubscriptionStatus,
  PortalUser,
  Priority,
  SecurityEvidence,
  SecurityEvidenceStatus,
  ServiceRow,
  ServiceStatus,
  SessionInfo,
  Snapshot,
  UserRole
} from "./types";

type ViewKey = "command" | "environments" | "services" | "incidents" | "logs" | "certificates" | "reports" | "security" | "users";

const viewKeys: ViewKey[] = ["command", "environments", "services", "incidents", "logs", "certificates", "reports", "security", "users"];

const defaultEnvOptions = [
  { id: "all", label: "All" },
  { id: "poc", label: "POC" },
  { id: "acc", label: "ACC" },
  { id: "prod", label: "PROD" }
];

const preferredCustomerOrder = ["KPN", "Directify", "Loginex"];

function customerSortRank(customer: string) {
  const rank = preferredCustomerOrder.findIndex((item) => item.toLowerCase() === customer.toLowerCase());
  return rank === -1 ? Number.MAX_SAFE_INTEGER : rank;
}

function environmentShortLabel(environment: Pick<EnvironmentSummary, "label" | "portfolioName">) {
  const label = environment.label || "";
  const customer = environment.portfolioName
    .replace(/\s+Monitoring$/i, "")
    .replace(/\s+Managed Service$/i, "")
    .trim();
  if (customer && label.toLowerCase().startsWith(customer.toLowerCase())) {
    return label.slice(customer.length).trim() || label;
  }
  return label;
}

function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

function aggregateDshStatus(environments: Snapshot["integrationHealth"]["dsh"]["environments"]) {
  if (!environments.length) return "not_configured";
  if (environments.every((environment) => environment.status === "connected")) return "connected";
  if (environments.some((environment) => environment.status === "connected")) return "partial";
  if (environments.some((environment) => environment.status === "error")) return "error";
  return "not_configured";
}

function aggregateGrafanaStatus(environments: Snapshot["integrationHealth"]["grafana"]["environments"]) {
  if (!environments.length) return "not_configured";
  if (environments.every((environment) => environment.status === "connected")) return "connected";
  if (environments.some((environment) => environment.status === "connected")) return "partial";
  if (environments.some((environment) => environment.status === "error")) return "error";
  if (environments.some((environment) => environment.status === "configured")) return "configured";
  if (environments.some((environment) => environment.status === "links_only")) return "links_only";
  return "not_configured";
}

function aggregateLogIngestStatus(environments: Snapshot["integrationHealth"]["logIngest"]["environments"]) {
  if (!environments.length) return "not_configured";
  if (environments.every((environment) => environment.status === "connected")) return "connected";
  if (environments.some((environment) => environment.status === "connected" || environment.status === "partial")) return "partial";
  if (environments.some((environment) => environment.status === "error")) return "error";
  if (environments.some((environment) => environment.status === "configured")) return "configured";
  return "not_configured";
}

function isDigitalOceanResource(service: ServiceRow) {
  const source = String(service.evidence.resources?.source || "");
  return source === "digitalocean" || source.startsWith("digitalocean-");
}

function aggregateDigitalOceanStatus(rows: ServiceRow[], configured: boolean) {
  const doRows = rows.filter(isDigitalOceanResource);
  if (!configured) return "not_configured";
  if (!doRows.length) return "configured";
  if (doRows.every((row) => row.evidence.resources?.status === "connected")) return "connected";
  if (doRows.some((row) => row.evidence.resources?.status === "connected")) return "partial";
  if (doRows.some((row) => row.evidence.resources?.status === "error")) return "error";
  return "configured";
}

function scopedIntegrationHealth(snapshot: Snapshot, environments: EnvironmentSummary[], services: ServiceRow[]): Snapshot["integrationHealth"] {
  const environmentIds = new Set(environments.map((environment) => environment.id));
  const dshEnvironments = snapshot.integrationHealth.dsh.environments.filter((environment) => environmentIds.has(environment.id));
  const grafanaEnvironments = snapshot.integrationHealth.grafana.environments.filter((environment) => environmentIds.has(environment.id));
  const logIngestEnvironments = snapshot.integrationHealth.logIngest.environments.filter((environment) => environmentIds.has(environment.id));
  const doRows = services.filter(isDigitalOceanResource);
  const logEventCount = logIngestEnvironments.reduce((total, environment) => total + Number(environment.events || 0), 0);

  return {
    ...snapshot.integrationHealth,
    dsh: {
      ...snapshot.integrationHealth.dsh,
      status: aggregateDshStatus(dshEnvironments),
      environments: dshEnvironments
    },
    grafana: {
      ...snapshot.integrationHealth.grafana,
      status: aggregateGrafanaStatus(grafanaEnvironments),
      environments: grafanaEnvironments
    },
    logIngest: {
      ...snapshot.integrationHealth.logIngest,
      status: aggregateLogIngestStatus(logIngestEnvironments),
      eventCount: logEventCount,
      heartbeatCount: logIngestEnvironments.filter((environment) => ["connected", "partial", "error"].includes(environment.status)).length,
      environments: logIngestEnvironments
    },
    digitalOcean: {
      ...snapshot.integrationHealth.digitalOcean,
      status: aggregateDigitalOceanStatus(doRows, Boolean(snapshot.integrationHealth.digitalOcean?.configured)),
      serviceCount: doRows.length,
      connected: doRows.filter((row) => row.evidence.resources?.status === "connected").length,
      errors: doRows.filter((row) => row.evidence.resources?.status === "error").length
    }
  };
}

function scopedMetrics(snapshot: Snapshot, portfolios: Snapshot["portfolios"], services: ServiceRow[], incidents: Incident[], probes: EndpointProbeRow[], integrations: Snapshot["integrationHealth"]) {
  const resourceUsageChecks = services.filter((service) => service.evidence.resources?.status === "connected").length;
  const resourceUsageWarnings = services.filter((service) =>
    ["warning", "critical"].includes(service.evidence.resources?.cpu?.status) ||
    ["warning", "critical"].includes(service.evidence.resources?.memory?.status)
  ).length;
  return {
    ...snapshot.metrics,
    portfolios: portfolios.length,
    serviceCount: services.length,
    healthy: services.filter((service) => service.status === "healthy").length,
    degraded: services.filter((service) => service.status === "degraded").length,
    down: services.filter((service) => service.status === "down").length,
    unknown: services.filter((service) => service.status === "unknown").length,
    liveDshChecks: services.filter((service) => service.evidence.dsh.status === "connected").length,
    liveGrafanaChecks: services.filter((service) => service.evidence.grafana.status === "connected").length,
    openIncidents: incidents.length,
    criticalIncidents: incidents.filter((incident) => incident.priority === "critical").length,
    endpointProbes: probes.length,
    endpointProbeFailures: probes.filter((probe) => probe.status === "critical" || probe.status === "warning").length,
    forwardedLogEvents: integrations.logIngest.eventCount,
    dshStatus: integrations.dsh.status,
    grafanaStatus: integrations.grafana.status,
    digitalOceanStatus: integrations.digitalOcean?.status || "not_configured",
    resourceUsageChecks,
    resourceUsageWarnings,
    logEvidenceStatus: integrations.logIngest.status !== "not_configured" ? integrations.logIngest.status : integrations.grafana.status,
    logIngestStatus: integrations.logIngest.status,
    slackStatus: integrations.slack.status
  };
}

function scopedReportChecklist(
  snapshot: Snapshot,
  portfolio: Snapshot["portfolios"][number] | undefined,
  environments: EnvironmentSummary[],
  services: ServiceRow[],
  incidents: Incident[],
  probes: EndpointProbeRow[],
  integrations: Snapshot["integrationHealth"]
) {
  const contractPortfolioId = snapshot.contract?.portfolioId || "kpn-fiam";
  if (portfolio?.id === contractPortfolioId) {
    return snapshot.reportChecklist;
  }
  const failingProbes = probes.filter((probe) => probe.status === "critical" || probe.status === "warning").length;
  const resourceChecks = services.filter((service) => service.evidence.resources?.status === "connected").length;
  const resourceWarnings = services.filter((service) =>
    ["warning", "critical"].includes(service.evidence.resources?.cpu?.status) ||
    ["warning", "critical"].includes(service.evidence.resources?.memory?.status)
  ).length;
  return [
    {
      section: "Customer Scope",
      status: services.length ? "complete" : "pending",
      details: `${portfolio?.name || "Selected customer"}: ${services.length} monitored checks across ${environments.length} environment(s)`
    },
    {
      section: "Endpoint Availability",
      status: failingProbes ? "critical" : probes.length ? "complete" : "pending",
      details: probes.length ? `${probes.length - failingProbes}/${probes.length} endpoint probes healthy` : "No endpoint probes configured"
    },
    {
      section: "Resource Evidence",
      status: resourceWarnings ? "warning" : resourceChecks ? "complete" : "pending",
      details: resourceChecks ? `${resourceChecks} CPU/memory checks connected` : "DigitalOcean resource metrics are not configured for this scope"
    },
    {
      section: "Alert Routing",
      status: integrations.slack.paused ? "warning" : "complete",
      details: integrations.slack.paused ? "Slack alerts are paused by an admin" : `Alerts route to ${integrations.slack.channel || "configured Slack channel"}`
    },
    {
      section: "Open Incidents",
      status: incidents.some((incident) => incident.priority === "critical") ? "critical" : incidents.length ? "warning" : "complete",
      details: `${incidents.length} open incident(s) in the selected customer scope`
    }
  ] satisfies Snapshot["reportChecklist"];
}

function time(value: string) {
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function since(value: string) {
  return new Intl.DateTimeFormat("en-CA", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function titleCase(value: string) {
  return value.replace(/[_-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function securityStatusTone(status: SecurityEvidenceStatus): "good" | "warn" | "bad" | "neutral" {
  if (status === "complete") return "good";
  if (status === "critical") return "bad";
  if (status === "warning") return "warn";
  return "neutral";
}

function formatValue(value: number | null, suffix = "") {
  return value === null ? "pending" : `${value}${suffix}`;
}

function percentValue(value: number | null) {
  return value === null ? "pending" : `${value.toFixed(3)}%`;
}

function compactPercent(value: number | null) {
  return value === null ? "n/a" : `${value.toFixed(value >= 10 ? 0 : 1)}%`;
}

function compactCpu(value: number | null) {
  return value === null ? "n/a" : `${value.toFixed(value >= 10 ? 0 : value >= 1 ? 1 : 2).replace(/\.?0+$/, "")}vCPU`;
}

function compactMb(value: number | null) {
  if (value === null) return "n/a";
  if (value >= 1024) return `${(value / 1024).toFixed(value >= 10240 ? 0 : 1)}GB`;
  return `${value.toFixed(0)}MB`;
}

function resourceTone(service: ServiceRow): TrafficTone {
  const resources = service.evidence.resources;
  if (!resources || resources.status === "not_applicable") return "neutral";
  if (resources.cpu.status === "critical" || resources.memory.status === "critical" || resources.status === "error") return "bad";
  if (resources.cpu.status === "warning" || resources.memory.status === "warning" || resources.status === "not_configured") return "warn";
  if (resources.status === "connected") return "good";
  return "neutral";
}

function resourceLabel(service: ServiceRow) {
  const resources = service.evidence.resources;
  if (!resources) return "Resource metrics missing";
  const allocation = resources.cpu.provisionedCpus !== null || resources.memory.provisionedMb !== null
    ? `${compactCpu(resources.cpu.provisionedCpus)} · ${compactMb(resources.memory.provisionedMb)}`
    : "";
  const usage = `use ${compactPercent(resources.cpu.usagePercent)} / ${compactPercent(resources.memory.usagePercent)}`;
  if (resources.status === "connected") {
    if (resources.source === "dsh-runtime" || resources.source === "dsh-config" || resources.source.startsWith("dsh-")) {
      return allocation ? `${allocation} · ${usage}` : `CPU/MEM ${usage}`;
    }
    return allocation ? `${usage} · alloc ${allocation}` : `CPU/MEM ${usage}`;
  }
  if (resources.status === "configured") {
    const cpu = resources.cpu.provisionedCpus === null ? "CPU n/a" : `CPU ${compactCpu(resources.cpu.provisionedCpus)}`;
    const memory = resources.memory.provisionedMb === null ? "MEM n/a" : `MEM ${compactMb(resources.memory.provisionedMb)}`;
    return `${cpu} · ${memory} allocated`;
  }
  return resources.message || titleCase(resources.status);
}

function resourceSourceLabel(source: string) {
  if (source === "digitalocean-app") return "DigitalOcean App";
  if (source === "digitalocean-droplet") return "DigitalOcean Droplet";
  if (source === "dsh-log-bridge-runtime" || source === "dsh-prometheus-bridge") return "DSH Prometheus bridge";
  if (source === "dsh-config") return "DSH config";
  if (source === "dsh-api") return "DSH API";
  return titleCase(source || "unknown");
}

function logBridgeReady(snapshot: Snapshot) {
  return snapshot.integrationHealth.logIngest.status === "partial" || snapshot.integrationHealth.logIngest.status === "connected";
}

function logSourceLabel(snapshot: Snapshot) {
  if (snapshot.integrationHealth.logIngest.status === "connected") return "DSH log bridge live";
  if (snapshot.integrationHealth.logIngest.status === "partial") return "DSH log bridge partial";
  if (snapshot.integrationHealth.logIngest.status === "error") return "DSH log bridge query failure";
  if (snapshot.integrationHealth.logIngest.status === "configured") return "DSH log bridge ready";
  if (snapshot.integrationHealth.grafana.status === "connected") return "Grafana/Loki live";
  return "Log source pending";
}

function logSourceTone(snapshot: Snapshot) {
  if (snapshot.integrationHealth.logIngest.status === "error") return "bad" as const;
  if (logBridgeReady(snapshot) || snapshot.metrics.liveGrafanaChecks) return "good" as const;
  return "warn" as const;
}

function serviceLogEvidence(service: ServiceRow, snapshot: Snapshot) {
  const criticalProbe = service.evidence.endpointProbes.find((probe) => probe.status === "critical");
  if (criticalProbe) {
    return { label: "Probe failed", source: criticalProbe.message, tone: "bad" as const };
  }
  const warningProbe = service.evidence.endpointProbes.find((probe) => probe.status === "warning");
  if (warningProbe) {
    return { label: "Probe warning", source: warningProbe.message, tone: "warn" as const };
  }
  const logErrorCount = Number(service.grafanaErrorCount || 0);
  const evidenceEventCount = service.evidence.grafana.logEvents.length;
  if (logErrorCount > 0 || evidenceEventCount > 0) {
    const resourceEvents = service.evidence.grafana.logEvents.filter((event) => event.source === "dsh-prometheus-bridge").length;
    const label =
      resourceEvents && resourceEvents === evidenceEventCount
        ? `${resourceEvents} resource alert${resourceEvents === 1 ? "" : "s"}`
        : `${Math.max(logErrorCount, evidenceEventCount)} evidence event${Math.max(logErrorCount, evidenceEventCount) === 1 ? "" : "s"}`;
    return {
      label,
      source: resourceEvents ? "Prometheus + Loki evidence" : "Live log evidence",
      tone: logErrorCount > 0 ? ("bad" as const) : ("warn" as const)
    };
  }
  if (service.evidence.grafana.status === "connected") {
    return { label: "0 log errors", source: "Loki live", tone: "good" as const };
  }
  const bridgeEnvironment = snapshot.integrationHealth.logIngest.environments.find((environment) => environment.id === service.environment);
  if (bridgeEnvironment?.queryFailures) {
    const fullFailure = bridgeEnvironment.serviceCount !== null && bridgeEnvironment.queryFailures >= bridgeEnvironment.serviceCount;
    return {
      label: fullFailure ? "Logs blocked" : "Logs partial",
      source: `${bridgeEnvironment.queryFailures}/${bridgeEnvironment.serviceCount || "?"} Loki queries failed`,
      tone: fullFailure ? ("bad" as const) : ("warn" as const)
    };
  }
  if (snapshot.integrationHealth.logIngest.status === "connected") {
    return {
      label: "No log errors",
      source: `${snapshot.integrationHealth.logIngest.heartbeatCount}/${snapshot.integrationHealth.logIngest.environments.length} env heartbeats`,
      tone: "good" as const
    };
  }
  if (snapshot.integrationHealth.logIngest.status === "partial") {
    return {
      label: "Logs partial",
      source: `${snapshot.integrationHealth.logIngest.heartbeatCount}/${snapshot.integrationHealth.logIngest.environments.length} env heartbeats`,
      tone: "warn" as const
    };
  }
  if (snapshot.integrationHealth.logIngest.status === "configured") {
    return { label: "Logs ready", source: "Waiting for heartbeats", tone: "warn" as const };
  }
  return { label: titleCase(service.evidence.grafana.status), source: "No log source", tone: "warn" as const };
}

function serviceLogMessage(service: ServiceRow, snapshot: Snapshot) {
  const failingProbe = service.evidence.endpointProbes.find((probe) => probe.status === "critical" || probe.status === "warning");
  if (failingProbe) {
    return `${failingProbe.name}: ${failingProbe.message}`;
  }
  const bridgeEnvironment = snapshot.integrationHealth.logIngest.environments.find((environment) => environment.id === service.environment);
  if (bridgeEnvironment?.queryFailures) {
    return `DSH log bridge heartbeat is live, but Loki reads are failing for ${bridgeEnvironment.label}: ${bridgeEnvironment.lastError || `${bridgeEnvironment.queryFailures} query failure(s)`}.`;
  }
  if (logBridgeReady(snapshot) && service.evidence.grafana.status !== "connected" && !service.evidence.grafana.logEvents.length) {
    return `DSH log bridge is ${snapshot.integrationHealth.logIngest.status}; direct Grafana/Loki API reads are not required for service error detection.`;
  }
  return service.evidence.grafana.message;
}

type TrafficTone = "good" | "warn" | "bad" | "neutral";

function serviceStatusTone(status: ServiceStatus): TrafficTone {
  if (status === "healthy") return "good";
  if (status === "down") return "bad";
  if (status === "degraded" || status === "maintenance") return "warn";
  return "neutral";
}

function dshStatusTone(status: ServiceRow["evidence"]["dsh"]["status"]): TrafficTone {
  if (status === "connected") return "good";
  if (status === "error" || status === "not_found") return "bad";
  if (status === "not_configured") return "warn";
  return "neutral";
}

function probeStatusTone(service: ServiceRow): TrafficTone {
  const statusProbes = service.evidence.endpointProbes.filter((probe) => (probe.source || "catalog") === "catalog" && (probe.kind || "http") !== "derived" && (probe.kind || "http") !== "tls");
  if (statusProbes.some((probe) => probe.status === "critical")) return "bad";
  if (statusProbes.some((probe) => probe.status === "warning")) return "warn";
  if (service.evidence.endpointProbes.some((probe) => probe.status === "critical" || probe.status === "warning")) return "warn";
  if (service.evidence.endpointProbes.length) return "good";
  return "neutral";
}

function probeStatusLabel(service: ServiceRow) {
  const statusProbes = service.evidence.endpointProbes.filter((probe) => (probe.source || "catalog") === "catalog" && (probe.kind || "http") !== "derived" && (probe.kind || "http") !== "tls");
  const critical = statusProbes.filter((probe) => probe.status === "critical").length;
  const warning = statusProbes.filter((probe) => probe.status === "warning").length;
  const advisory = service.evidence.endpointProbes.filter((probe) => !statusProbes.includes(probe) && (probe.status === "critical" || probe.status === "warning")).length;
  if (critical) return `${critical} probe failure${critical === 1 ? "" : "s"}`;
  if (warning) return `${warning} probe warning${warning === 1 ? "" : "s"}`;
  if (advisory) return `${advisory} advisory probe warning${advisory === 1 ? "" : "s"}`;
  if (service.evidence.endpointProbes.length) return `${service.evidence.endpointProbes.length} probe${service.evidence.endpointProbes.length === 1 ? "" : "s"} ok`;
  return "No endpoint probe";
}

function TrafficLight({ tone, label }: { tone: TrafficTone; label: string }) {
  return <span className={cx("trafficLight", tone)} title={label} aria-label={label} />;
}

function TrafficLegend({ tone, label }: { tone: TrafficTone; label: string }) {
  return (
    <span className="trafficLegendItem">
      <TrafficLight tone={tone} label={label} />
      {label}
    </span>
  );
}

function formatDate(value: string | null) {
  if (!value) return "pending";
  return new Intl.DateTimeFormat("en-GB", { year: "numeric", month: "short", day: "2-digit" }).format(new Date(value));
}

function labelText(labels: Record<string, string>) {
  const keys = ["location", "service", "queriedService", "app", "container", "pod", "job", "namespace", "filename"];
  return keys.map((key) => labels[key]).filter(Boolean).slice(0, 2).join(" / ");
}

function logLabelDetails(labels: Record<string, string>) {
  const keys = [
    ["service", "service"],
    ["queriedService", "query"],
    ["app", "app"],
    ["container", "container"],
    ["pod", "pod"],
    ["job", "job"],
    ["namespace", "namespace"],
    ["filename", "file"],
    ["logger", "logger"],
    ["thread", "thread"],
    ["traceId", "trace"],
    ["spanId", "span"],
    ["exception", "exception"],
    ["metricCategory", "metric"],
    ["metricValue", "value"],
    ["metricThreshold", "threshold"],
    ["grafanaValueString", "value"]
  ] as const;
  return keys
    .map(([key, label]) => ({ key, label, value: labels[key] }))
    .filter((item) => Boolean(item.value))
    .slice(0, 10);
}

function LogEventDetails({ event }: { event: LogEventRow }) {
  const details = logLabelDetails(event.labels);
  if (!details.length) return null;
  return (
    <div className="logEventDetails" aria-label="Log event labels">
      {details.map((detail) => (
        <span key={`${detail.key}:${detail.value}`}>
          <b>{detail.label}</b>
          {detail.value}
        </span>
      ))}
    </div>
  );
}

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month: string) {
  const match = month.match(/^(\d{4})-(\d{2})$/);
  if (!match) return month;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(date);
}

function recentReportMonths(count = 18) {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(Date.UTC(now.getFullYear(), now.getMonth() - index, 1));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
  });
}

function hashRoute(hash = window.location.hash) {
  const raw = hash.replace(/^#/, "");
  const queryIndex = raw.indexOf("?");
  const view = queryIndex === -1 ? raw : raw.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : raw.slice(queryIndex + 1);
  return { view, params: new URLSearchParams(query) };
}

function viewFromHash(hash = window.location.hash): ViewKey {
  const { view } = hashRoute(hash);
  return viewKeys.includes(view as ViewKey) ? (view as ViewKey) : "command";
}

function incidentIdFromHash(hash = window.location.hash) {
  const { view, params } = hashRoute(hash);
  return view === "incidents" ? params.get("incident") || "" : "";
}

function incidentHash(incidentId: string) {
  return `#incidents?incident=${encodeURIComponent(incidentId)}`;
}

function incidentElementId(incidentId: string) {
  return `incident-${encodeURIComponent(incidentId).replace(/%/g, "_")}`;
}

const SNAPSHOT_AUTO_REFRESH_MS = 60_000;

function StatusBadge({ status }: { status: ServiceStatus }) {
  const icons = {
    healthy: <CheckCircle2 size={15} />,
    degraded: <AlertTriangle size={15} />,
    down: <XCircle size={15} />,
    maintenance: <Pause size={15} />,
    unknown: <Server size={15} />
  };
  return (
    <span className={cx("statusBadge", status)}>
      {icons[status]}
      {status}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: Priority }) {
  return <span className={cx("priorityBadge", priority)}>{priority}</span>;
}

function Sparkline({ points }: { points: Array<number | null> }) {
  const validPoints = points
    .map((point, index) => (typeof point === "number" ? { point, index } : null))
    .filter((item): item is { point: number; index: number } => Boolean(item));
  if (!validPoints.length) {
    return <div className="sparklineEmpty">Live metrics pending</div>;
  }

  const width = 160;
  const height = 38;
  const plotPoints = validPoints.length === 1
    ? [{ point: validPoints[0].point, index: 0 }, { point: validPoints[0].point, index: Math.max(1, points.length - 1) }]
    : validPoints;
  const d = plotPoints
    .map(({ point, index }, pointIndex) => {
      const x = (index / Math.max(1, points.length - 1)) * width;
      const y = height - (Math.max(0, Math.min(100, point)) / 100) * (height - 8) - 4;
      return `${pointIndex === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={`${d} L ${width} ${height} L 0 ${height} Z`} className="sparklineFill" />
      <path d={d} className="sparklineLine" />
    </svg>
  );
}

function uptimeWindowLabel(service: ServiceRow) {
  if (!service.uptimeSampleCount) {
    return "Waiting for first 5-minute sample";
  }
  const start = service.uptimeWindowStart ? since(service.uptimeWindowStart) : "recently";
  return `${service.uptimeSampleCount} sample${service.uptimeSampleCount === 1 ? "" : "s"} since ${start}`;
}

function Sidebar(props: { activeView: ViewKey; criticalCount: number; openIncidentCount: number; session?: SessionInfo | null; onViewChange: (view: ViewKey) => void }) {
  const items: Array<{ key: ViewKey; icon: ReactNode; label: string; count?: number }> = [
    { key: "command", icon: <BarChart3 size={18} />, label: "Command" },
    { key: "environments", icon: <Layers3 size={18} />, label: "Environments" },
    { key: "services", icon: <Box size={18} />, label: "Services" },
    { key: "incidents", icon: <Siren size={18} />, label: "Incidents", count: props.criticalCount || props.openIncidentCount },
    { key: "logs", icon: <TerminalSquare size={18} />, label: "Logs" },
    { key: "certificates", icon: <Shield size={18} />, label: "Certificates" },
    { key: "reports", icon: <FileText size={18} />, label: "Reports" },
    ...(props.session?.permissions.canManageUsers ? [{ key: "security" as ViewKey, icon: <ShieldCheck size={18} />, label: "Security" }] : []),
    { key: "users", icon: <Users size={18} />, label: props.session?.permissions.canManageUsers ? "Users" : "Access" }
  ];
  return (
    <aside className="sidebar">
      <div className="brand">
        <img className="brandLogo" src="/protium-logo-white.svg" alt="" />
        <span>Protium</span>
      </div>
      <nav>
        {items.map((item) => {
          const label = `${item.label}${item.count ? ` (${item.count})` : ""}`;
          return (
            <button
              className={cx("navItem", props.activeView === item.key && "active")}
              key={item.key}
              aria-label={label}
              aria-current={props.activeView === item.key ? "page" : undefined}
              title={label}
              onClick={() => props.onViewChange(item.key)}
            >
              {item.icon}
              <span>{label}</span>
            </button>
          );
        })}
      </nav>
      <div className="sidebarFooter">
        <span className="sourceDot" />
        <div>
          <strong>{props.session?.user?.role === "admin" ? "Admin mode" : "Read-only mode"}</strong>
          <span>{props.session?.user?.username || "DSH and log bridge"}</span>
        </div>
      </div>
    </aside>
  );
}

function Header(props: {
  session?: SessionInfo | null;
  portfolioOptions: Array<{ id: string; customer: string; name: string }>;
  selectedPortfolioId: string;
  setSelectedPortfolioId: (value: string) => void;
  environmentOptions: Array<{ id: string; label: string }>;
  selectedEnv: string;
  setSelectedEnv: (value: string) => void;
  search: string;
  setSearch: (value: string) => void;
  onRefresh: () => void;
  onReport: () => void;
  onDownload: () => void;
  loading: boolean;
  reportMonth: string;
  setReportMonth: (value: string) => void;
  onLogout: () => void;
}) {
  return (
    <header className="topbar">
      <div className="topbarTitle">
        <div>
          <h1>Protium Monitoring</h1>
          <p>Live DSH and log evidence for FIAM Site Reliability Engineering</p>
        </div>
      </div>
      <div className="topbarControls">
        <div className="segmented customerSegment" aria-label="Customer scope">
          {props.portfolioOptions.map((option) => (
            <button
              key={option.id}
              className={props.selectedPortfolioId === option.id ? "selected" : ""}
              title={option.name}
              onClick={() => props.setSelectedPortfolioId(option.id)}
            >
              {option.customer}
            </button>
          ))}
        </div>
        <div className="segmented environmentSegment" aria-label="Environment scope">
          {props.environmentOptions.map((option) => (
            <button key={option.id} className={props.selectedEnv === option.id ? "selected" : ""} onClick={() => props.setSelectedEnv(option.id)}>
              {option.label}
            </button>
          ))}
        </div>
        <label className="searchBox">
          <Search size={16} />
          <input value={props.search} onChange={(event) => props.setSearch(event.target.value)} placeholder="Search services, incidents, logs" />
        </label>
        <label className="monthBox" title="Monthly report period">
          <CalendarDays size={16} />
          <input type="month" value={props.reportMonth} onChange={(event) => props.setReportMonth(event.target.value)} />
        </label>
        <button className="iconButton" onClick={props.onRefresh} aria-label="Refresh snapshot">
          <RefreshCw size={17} className={props.loading ? "spin" : ""} />
        </button>
        <button className="primaryButton" onClick={props.onDownload}>
          <Download size={16} />
          Download PDF
        </button>
        <div className="securityChip">
          <Lock size={14} />
          {props.session?.user?.role === "admin" ? "Admin" : props.session?.access?.customers.map((customer) => customer.name).join(", ") || "No scope"}
        </div>
        <button className="iconButton" onClick={props.onLogout} aria-label="Sign out" title="Sign out">
          <LogOut size={17} />
        </button>
      </div>
    </header>
  );
}

function SourceStrip({
  snapshot,
  environments,
  services,
  endpointProbes,
  canRunActions,
  onToggleSlackAlerts
}: {
  snapshot: Snapshot;
  environments: EnvironmentSummary[];
  services: ServiceRow[];
  endpointProbes: EndpointProbeRow[];
  canRunActions: boolean;
  onToggleSlackAlerts: () => void;
}) {
  const environmentIds = new Set(environments.map((environment) => environment.id));
  const dshEnvironments = snapshot.integrationHealth.dsh.environments.filter((environment) => environmentIds.has(environment.id));
  const grafanaEnvironments = snapshot.integrationHealth.grafana.environments.filter((environment) => environmentIds.has(environment.id));
  const logIngestEnvironments = snapshot.integrationHealth.logIngest.environments.filter((environment) => environmentIds.has(environment.id));
  const digitalOceanRows = services.filter((service) => String(service.evidence.resources?.source || "").startsWith("digitalocean"));
  const digitalOceanConnected = digitalOceanRows.filter((service) => service.evidence.resources?.status === "connected").length;
  const digitalOceanErrors = digitalOceanRows.filter((service) => service.evidence.resources?.status === "error").length;
  const grafana = snapshot.integrationHealth.grafana;
  const logIngest = snapshot.integrationHealth.logIngest;
  const slack = snapshot.integrationHealth.slack;
  const gitlab = snapshot.integrationHealth.gitlab;
  const endpointFailures = endpointProbes.filter((probe) => probe.status === "critical").length;
  return (
    <section className="sourceStrip" aria-label="Live source status">
      {dshEnvironments.map((environment) => (
        <div key={environment.id} className={cx("sourceChip", environment.status)}>
          <strong>DSH {environment.label}</strong>
          <span>{titleCase(environment.status)}</span>
          <em>{environment.connected}/{environment.serviceCount} configs</em>
        </div>
      ))}
      {grafanaEnvironments.length > 0 && grafana.status !== "connected" && (
        <div className={cx("sourceChip", grafana.status)}>
          <strong>Grafana/Loki</strong>
          <span>{titleCase(grafanaEnvironments.some((environment) => environment.status === "connected") ? "partial" : grafana.status)}</span>
          <em>{grafana.missing.length ? grafana.missing.join(", ") : `${grafanaEnvironments.length} environment(s)`}</em>
        </div>
      )}
      {logIngestEnvironments.length > 0 && (
        <div className={cx("sourceChip", logIngest.status)}>
          <strong>DSH Log Bridge</strong>
          <span>{titleCase(logIngest.status)}</span>
          <em>
            {logIngest.configured
              ? `${logIngestEnvironments.filter((environment) => ["connected", "partial", "error"].includes(environment.status)).length}/${logIngestEnvironments.length} heartbeats`
              : logIngest.missing.join(", ")}
          </em>
        </div>
      )}
      {digitalOceanRows.length > 0 && (
        <div className={cx("sourceChip", digitalOceanErrors ? "error" : digitalOceanConnected ? "connected" : "configured")}>
          <strong>DigitalOcean Metrics</strong>
          <span>{digitalOceanConnected ? "Connected" : digitalOceanErrors ? "Error" : "Configured"}</span>
          <em>{digitalOceanConnected}/{digitalOceanRows.length} CPU/MEM checks</em>
        </div>
      )}
      {canRunActions && gitlab?.configured && (
        <div className={cx("sourceChip", gitlab.status)}>
          <strong>GitLab CI/CD</strong>
          <span>{titleCase(gitlab.status)}</span>
          <em>{gitlab.connected}/{gitlab.projectCount} projects · {gitlab.securityJobs} scan jobs</em>
        </div>
      )}
      <div className={cx("sourceChip", endpointFailures ? "error" : "connected")}>
        <strong>Endpoint Probes</strong>
        <span>{endpointFailures ? "Action required" : "Healthy"}</span>
        <em>{endpointProbes.length - endpointFailures}/{endpointProbes.length} live probes</em>
      </div>
      <div className={cx("sourceChip", slack.status, "sourceChipWithAction")}>
        <strong>Slack Alerts</strong>
        <span>{titleCase(slack.status)}</span>
        <em>
          {slack.webhookConfigured
            ? slack.paused
              ? `${slack.channel} paused${slack.control?.updatedBy ? ` by ${slack.control.updatedBy}` : ""}`
              : `${slack.channel} - ${slack.alertPriorities.join(", ")} in ${(slack.alertEnvironments || ["acc", "prod"]).join(", ").toUpperCase()} x${slack.alertMinOccurrences || 2}`
            : slack.missing.join(", ")}
        </em>
        {slack.webhookConfigured && (
          <button
            className={cx("sourceChipButton", slack.paused ? "resume" : "pause")}
            disabled={!canRunActions}
            title={canRunActions ? (slack.paused ? "Resume Slack alerts" : "Pause Slack alerts") : "Admin role required"}
            onClick={onToggleSlackAlerts}
          >
            {slack.paused ? <Play size={13} /> : <Pause size={13} />}
            {slack.paused ? "Resume" : "Pause"}
          </button>
        )}
      </div>
    </section>
  );
}

function IncidentQueue({ incidents, activeId, onSelect }: { incidents: Incident[]; activeId?: string; onSelect: (incident: Incident) => void }) {
  return (
    <section className="panel incidentPanel">
      <div className="panelHeader">
        <h2>Priority Incident Queue</h2>
        <span>{incidents.length}</span>
      </div>
      <div className="incidentList">
        {incidents.slice(0, 5).map((incident) => (
          <button
            key={incident.id}
            id={incidentElementId(incident.id)}
            className={cx("incidentRow", activeId === incident.id && "selected")}
            onClick={() => onSelect(incident)}
          >
            <PriorityBadge priority={incident.priority} />
            <strong className="incidentTitle">{incident.title}</strong>
            <span className="incidentService">{incident.serviceName}</span>
            <span className="incidentEnv">{incident.environmentLabel}</span>
            <span className="incidentTime">{since(incident.since)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function UptimeStrip({ environments, snapshot }: { environments: EnvironmentSummary[]; snapshot: Snapshot }) {
  return (
    <section className="panel uptimePanel">
      <div className="panelHeader">
        <h2>Live Evidence by Environment</h2>
        <a href="#services">
          View details <ChevronRight size={14} />
        </a>
      </div>
      <div className="uptimeGrid">
        {environments.map((environment) => (
          <div key={environment.id} className="uptimeCell">
            <span>{environment.label}</span>
            <div>
              <strong>{environment.slaTarget}% SLA</strong>
              <strong className={environment.slaStatus === "met" || environment.slaStatus === "best_effort" ? "good" : "warn"}>{titleCase(environment.slaStatus)}</strong>
            </div>
            <p>
              {environment.liveDsh}/{environment.serviceCount} DSH configs,{" "}
              {environment.liveLogs ? `${environment.liveLogs} Loki checks` : logBridgeReady(snapshot) ? `log bridge ${snapshot.integrationHealth.logIngest.status}` : "0 log checks"}
            </p>
            <Sparkline points={environment.sparkline} />
          </div>
        ))}
      </div>
    </section>
  );
}

const environmentSort: Record<string, number> = { prod: 0, acc: 1, poc: 2 };
const serviceStatusSort: Record<ServiceStatus, number> = { down: 0, degraded: 1, unknown: 2, maintenance: 3, healthy: 4 };

function serviceHasProblem(service: ServiceRow) {
  return service.status === "down" || service.status === "degraded" || service.status === "unknown";
}

function sortedProblemServices(services: ServiceRow[]) {
  return services
    .filter(serviceHasProblem)
    .sort(
      (left, right) =>
        serviceStatusSort[left.status] - serviceStatusSort[right.status] ||
        (environmentSort[left.environment] ?? 9) - (environmentSort[right.environment] ?? 9) ||
        left.serviceName.localeCompare(right.serviceName)
    );
}

function MobileServiceIssueCards(props: { services: ServiceRow[]; snapshot: Snapshot; onSelect: (service: ServiceRow) => void; embedded?: boolean }) {
  const issueServices = sortedProblemServices(props.services);
  const className = cx("mobileOnly mobileServiceIssues", !props.embedded && "panel", !issueServices.length && "clear");
  if (!issueServices.length) {
    return (
      <section className={className} data-testid="mobile-service-issues">
        <div className="mobileIssueSectionHeader">
          <strong>No failing services</strong>
          <span>Current scope</span>
        </div>
      </section>
    );
  }

  return (
    <section className={className} data-testid="mobile-service-issues">
      <div className="mobileIssueSectionHeader">
        <strong>Affected services</strong>
        <span>{issueServices.length}</span>
      </div>
      <div className="mobileIssueList">
        {issueServices.slice(0, 10).map((service) => {
          const logEvidence = serviceLogEvidence(service, props.snapshot);
          return (
            <button key={service.id} className={cx("mobileServiceCard", service.status)} onClick={() => props.onSelect(service)}>
              <div>
                <StatusBadge status={service.status} />
                <span>{service.environmentLabel}</span>
              </div>
              <strong>{service.serviceName}</strong>
              <em>{logEvidence.source || activeSignalText(service)}</em>
              <small className={cx("mobileResourceLine", resourceTone(service))}>{resourceLabel(service)}</small>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function activeSignalText(service: ServiceRow) {
  const runtime = service.evidence.dsh.runtime?.message;
  if (runtime && service.evidence.dsh.runtime?.status !== "unknown") return runtime;
  if (service.evidence.dsh.notifications[0]) return service.evidence.dsh.notifications[0];
  if (service.evidence.dsh.status !== "connected") return service.evidence.dsh.message;
  return "Status needs attention.";
}

function MobileIssueOverview(props: {
  incidents: Incident[];
  services: ServiceRow[];
  snapshot: Snapshot;
  activeId?: string;
  onSelectIncident: (incident: Incident) => void;
  onSelectService: (service: ServiceRow) => void;
  onOpenIncidents: () => void;
}) {
  const issueServices = sortedProblemServices(props.services);
  const down = props.services.filter((service) => service.status === "down").length;
  const degraded = props.services.filter((service) => service.status === "degraded" || service.status === "unknown").length;
  const hasIssues = props.incidents.length > 0 || issueServices.length > 0;

  return (
    <section className={cx("panel mobileOnly mobileIssueOverview", !hasIssues && "clear")} data-testid="mobile-issue-overview">
      <div className="mobileIssueHero">
        <span>Current status</span>
        <strong>{hasIssues ? "Action required" : "All clear"}</strong>
        <p>
          {hasIssues
            ? `${props.incidents.length} incidents, ${down} down, ${degraded} degraded or unknown`
            : "No active incidents in the current scope"}
        </p>
      </div>
      <div className="mobileIssueStats">
        <div>
          <span>Incidents</span>
          <strong className={props.incidents.length ? "warn" : "good"}>{props.incidents.length}</strong>
        </div>
        <div>
          <span>Down</span>
          <strong className={down ? "bad" : "good"}>{down}</strong>
        </div>
        <div>
          <span>Attention</span>
          <strong className={degraded ? "warn" : "good"}>{degraded}</strong>
        </div>
      </div>
      {props.incidents.length ? (
        <div className="mobileIssueList" data-testid="mobile-incident-list">
          {props.incidents.slice(0, 4).map((incident) => (
            <button key={incident.id} className={cx("mobileIncidentCard", incident.priority, props.activeId === incident.id && "selected")} onClick={() => props.onSelectIncident(incident)}>
              <div>
                <PriorityBadge priority={incident.priority} />
                <span>{incident.environmentLabel}</span>
              </div>
              <strong>{incident.title}</strong>
              <em>{incident.metric}</em>
            </button>
          ))}
          <button className="mobileTextAction" onClick={props.onOpenIncidents}>
            All incidents
            <ChevronRight size={14} />
          </button>
        </div>
      ) : null}
      <MobileServiceIssueCards services={props.services} snapshot={props.snapshot} onSelect={props.onSelectService} embedded />
    </section>
  );
}

function ServicesMatrix(props: {
  services: ServiceRow[];
  snapshot: Snapshot;
  selectedEnv: string;
  selectedServiceId?: string;
  onSelect: (service: ServiceRow) => void;
}) {
  const envs = (props.selectedEnv === "all"
    ? props.snapshot.environments.filter((environment) => props.services.some((service) => service.environment === environment.id))
    : props.snapshot.environments.filter((environment) => environment.id === props.selectedEnv));
  const envRecords = envs.length ? envs : props.selectedEnv === "all" ? defaultEnvOptions.slice(1) : [{ id: props.selectedEnv, label: props.selectedEnv.toUpperCase() }];
  const multiplePortfolios = new Set(props.services.map((service) => service.portfolioId)).size > 1;
  const rows = Array.from(
    props.services.reduce((acc, service) => {
      const key = `${service.portfolioId}:${service.serviceName}`;
      if (!acc.has(key)) {
        acc.set(key, {
          key,
          portfolioId: service.portfolioId,
          portfolioName: service.portfolioName,
          serviceName: service.serviceName
        });
      }
      return acc;
    }, new Map<string, { key: string; portfolioId: string; portfolioName: string; serviceName: string }>())
      .values()
  );
  return (
    <section className="panel servicesPanel" id="services">
      <div className="matrix" style={{ gridTemplateColumns: `12rem repeat(${envRecords.length}, minmax(9.5rem, 1fr))` }}>
        <div className="matrixHead serviceNameHead">Service</div>
        {envRecords.map((env) => (
          <div key={env.id} className="matrixHead envHead">
            {env.label}
          </div>
        ))}
        {rows.map((serviceRow) => (
          <div className="matrixRow" key={serviceRow.key}>
            <div className="serviceName">
              <Server size={15} />
              <span>{serviceRow.serviceName}</span>
              {multiplePortfolios && <small>{serviceRow.portfolioName}</small>}
            </div>
            {envRecords.map((env) => {
              const row = props.services.find((service) => service.portfolioId === serviceRow.portfolioId && service.serviceName === serviceRow.serviceName && service.environment === env.id);
              if (!row) {
                return <div key={env.id} className="serviceCell muted">Not configured</div>;
              }
              const logEvidence = serviceLogEvidence(row, props.snapshot);
              const dshLabel = `DSH ${titleCase(row.evidence.dsh.status)}${row.evidence.dsh.message ? `: ${row.evidence.dsh.message}` : ""}`;
              const logLabel = `Logs ${logEvidence.label}${logEvidence.source ? `: ${logEvidence.source}` : ""}`;
              const probeLabel = `Probes ${probeStatusLabel(row)}`;
              const resourceText = resourceLabel(row);
              const resourceAria = `Resources ${resourceText}; ${row.evidence.resources?.message || ""}`;
              return (
                <button
                  key={row.id}
                  className={cx("serviceCell", row.status, props.selectedServiceId === row.id && "selected")}
                  onClick={() => props.onSelect(row)}
                  aria-label={`${row.environmentLabel} ${row.serviceName}: ${row.status}, ${dshLabel}, ${logLabel}, ${probeLabel}, ${resourceAria}`}
                >
                  <div className="trafficLightSet" aria-hidden="true">
                    <TrafficLight tone={serviceStatusTone(row.status)} label={`Service ${titleCase(row.status)}`} />
                    <TrafficLight tone={dshStatusTone(row.evidence.dsh.status)} label={dshLabel} />
                    <TrafficLight tone={logEvidence.tone} label={logLabel} />
                    <TrafficLight tone={probeStatusTone(row)} label={probeLabel} />
                    <TrafficLight tone={resourceTone(row)} label={resourceAria} />
                  </div>
                  <span className={cx("resourceMini", resourceTone(row))}>{resourceText}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="legend">
        <TrafficLegend tone="good" label="OK" />
        <TrafficLegend tone="warn" label="Attention" />
        <TrafficLegend tone="bad" label="Down / failing" />
        <TrafficLegend tone="neutral" label="Not measured" />
      </div>
    </section>
  );
}

function DeepLink({ href, label }: { href?: string | null; label: string }) {
  if (!href) {
    return <span className="deepLink disabled">{label} not configured</span>;
  }
  return (
    <a className="deepLink" href={href} target="_blank" rel="noreferrer">
      {label}
      <ExternalLink size={13} />
    </a>
  );
}

function serviceVhostLinks(service: ServiceRow, snapshot: Snapshot) {
  const links = new Map<string, { url: string; label: string; meta: string }>();
  const addUrl = (url: string, meta: string) => {
    try {
      const parsed = new URL(url);
      const label = `${parsed.host}${parsed.pathname === "/" ? "" : parsed.pathname}`;
      links.set(url, { url, label, meta });
    } catch {
      // Ignore invalid catalog values; the evidence message still shows the source state.
    }
  };

  for (const vhost of service.evidence.dsh.vhosts || []) {
    addUrl(vhost.url, `DSH config - ${vhost.source || "vhost"}`);
  }

  for (const probe of service.evidence.endpointProbes) {
    addUrl(probe.url, `${probe.name} - ${probe.status}`);
  }

  for (const certificate of snapshot.certificates) {
    if (certificate.environment === service.environment && certificate.serviceId === service.serviceId) {
      addUrl(`https://${certificate.host}/`, `${certificate.name} - TLS ${certificate.status}`);
    }
  }

  return Array.from(links.values());
}

function serviceDshVhostLinks(service: ServiceRow) {
  const links = new Map<string, { url: string; label: string; meta: string }>();
  for (const vhost of service.evidence.dsh.vhosts || []) {
    try {
      const parsed = new URL(vhost.url);
      const label = `${parsed.host}${parsed.pathname === "/" ? "" : parsed.pathname}`;
      links.set(vhost.url, { url: vhost.url, label, meta: `DSH config - ${vhost.source || "vhost"}` });
    } catch {
      // Ignore invalid DSH values; the evidence message still carries the source.
    }
  }
  return Array.from(links.values());
}

type ServiceRouteDefinition = {
  value: string;
  url: string | null;
  label: string;
  source: string;
  evidence: "dsh" | "probe";
};

function routeValueFromUrl(url: string) {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname || "/"}${parsed.search || ""}`;
  } catch {
    return null;
  }
}

function serviceRouteDefinitions(service: ServiceRow): ServiceRouteDefinition[] {
  const routes: ServiceRouteDefinition[] = [];
  const seen = new Set<string>();
  const addRoute = (route: ServiceRouteDefinition) => {
    const key = `${route.evidence}:${route.value}:${route.url || ""}:${route.source}`;
    if (seen.has(key)) return;
    seen.add(key);
    routes.push(route);
  };

  for (const pathDefinition of service.evidence.dsh.pathDefinitions || []) {
    addRoute({
      value: pathDefinition.value,
      url: pathDefinition.url,
      label: pathDefinition.label || "path",
      source: pathDefinition.source,
      evidence: "dsh"
    });
  }

  for (const probe of service.evidence.endpointProbes || []) {
    const probeSource = probe.source || "catalog";
    if (probeSource !== "catalog" && probeSource !== "dsh-path") continue;
    const value = routeValueFromUrl(probe.url);
    if (!value) continue;
    addRoute({
      value: probe.routePath || value,
      url: probe.url,
      label: probe.name,
      source: `${probeSource}.${probe.id}.url (${probe.status})`,
      evidence: "probe"
    });
  }

  return routes;
}

function serviceResetSupport(service: ServiceRow, canRunActions: boolean) {
  if (!canRunActions) {
    return { supported: false, message: "Admin role required to reset services." };
  }
  if (service.monitoringSource === "endpoint" || service.evidence.dsh.status === "not_applicable") {
    return { supported: false, message: "External endpoint service; reset is handled outside the DSH API." };
  }
  if (!service.dshServiceName) {
    return { supported: false, message: "No DSH service name is linked to this row." };
  }
  if (service.evidence.dsh.status === "not_configured") {
    return { supported: false, message: "DSH API credentials are not configured for this environment." };
  }
  return {
    supported: true,
    message: `Requests a DSH service restart for ${service.dshServiceName}. Confirm the incident context before using this.`
  };
}

function servicePathDefinitionHref(pathDefinition: { value: string; url: string | null }, dshVhostLinks: Array<{ url: string }>) {
  if (pathDefinition.url) {
    return pathDefinition.url;
  }
  if (dshVhostLinks.length === 1 && (pathDefinition.value.startsWith("/") || /^[a-z0-9]/i.test(pathDefinition.value))) {
    try {
      const pathValue = pathDefinition.value.startsWith("/") ? pathDefinition.value : `/${pathDefinition.value}`;
      return new URL(pathValue, dshVhostLinks[0].url).toString();
    } catch {
      return null;
    }
  }
  return null;
}

function DetailDrawer(props: {
  service?: ServiceRow;
  incident?: Incident;
  snapshot: Snapshot;
  canRunActions: boolean;
  resetPendingId: string | null;
  onSend: (incident: Incident) => void;
  onResetService: (service: ServiceRow) => void;
}) {
  const activeService =
    props.service ||
    props.snapshot.services.find((row) => row.serviceId === props.incident?.serviceId && row.environment === props.incident?.environment) ||
    props.snapshot.services[0];

  if (!activeService) {
    return (
      <aside className="detailDrawer">
        <div className="drawerBlock">
          <h3>No service selected</h3>
          <p className="mutedCopy">Adjust the filters to show DSH services.</p>
        </div>
      </aside>
    );
  }

  const cert = props.snapshot.certificates.find((item) => item.serviceId === activeService.serviceId && item.environment === activeService.environment);
  const dshVhostLinks = serviceDshVhostLinks(activeService);
  const vhostLinks = serviceVhostLinks(activeService, props.snapshot);
  const routeDefinitions = serviceRouteDefinitions(activeService);
  const logEvidence = serviceLogEvidence(activeService, props.snapshot);
  const resetSupport = serviceResetSupport(activeService, props.canRunActions);
  const resetPending = props.resetPendingId === activeService.id;

  return (
    <aside className="detailDrawer">
      <div className="drawerHeader">
        <div>
          <h2>{activeService.serviceName}</h2>
          <p>{activeService.environmentLabel} - {activeService.product}</p>
        </div>
        <Shield size={20} />
      </div>
      {props.incident && (
        <div className="drawerBlock incidentFocus">
          <PriorityBadge priority={props.incident.priority} />
          <strong>{props.incident.title}</strong>
          <p>{props.incident.recommendedAction}</p>
          <button
            className="secondaryButton"
            disabled={!props.canRunActions || props.snapshot.integrationHealth.slack.paused}
            title={props.snapshot.integrationHealth.slack.paused ? "Slack alerts are paused" : props.canRunActions ? "Send to Slack" : "Admin role required"}
            onClick={() => props.onSend(props.incident!)}
          >
            <Send size={15} />
            {props.snapshot.integrationHealth.slack.paused ? "Slack paused" : "Notify Slack"}
          </button>
        </div>
      )}
      <div className="drawerBlock">
        <h3>Live Evidence</h3>
        <div className="metricTriplet">
          <div><span>Health</span><StatusBadge status={activeService.status} /></div>
          <div><span>DSH read</span><strong>{titleCase(activeService.evidence.dsh.status)}</strong></div>
          <div><span>Log evidence</span><strong className={logEvidence.tone}>{logEvidence.label}</strong></div>
        </div>
        <div className="uptimeEvidence">
          <div>
            <span>Synthetic uptime</span>
            <strong>{percentValue(activeService.uptime30d)}</strong>
          </div>
          <Sparkline points={activeService.uptimeSparkline || []} />
          <small>{uptimeWindowLabel(activeService)}</small>
        </div>
        <p className="evidenceMessage">{activeService.evidence.dsh.message}</p>
        <p className="evidenceMessage">
          DSH provisioned: {activeService.evidence.dsh.provisioned === null ? "unknown" : activeService.evidence.dsh.provisioned ? "yes" : "no"}.
          {" "}Runtime: {titleCase(activeService.evidence.dsh.runtime?.status || "unknown")}.
          {" "}Notifications: {activeService.evidence.dsh.notifications.length}.
        </p>
        {activeService.evidence.dsh.runtime?.message && (
          <p className="evidenceMessage">{activeService.evidence.dsh.runtime.message}</p>
        )}
        {activeService.evidence.dsh.notifications.length > 0 && (
          <p className="warningText">{activeService.evidence.dsh.notifications[0]}</p>
        )}
      </div>
      <div className="drawerBlock serviceActionBlock">
        <h3>Service Actions</h3>
        <button
          className="secondaryButton dangerButton"
          disabled={!resetSupport.supported || resetPending}
          title={resetSupport.message}
          onClick={() => props.onResetService(activeService)}
        >
          <RotateCcw size={15} className={resetPending ? "spin" : undefined} />
          {resetPending ? "Reset requested" : "Reset service"}
        </button>
        <p className={resetSupport.supported ? "evidenceMessage" : "warningText"}>{resetSupport.message}</p>
      </div>
      <div className="drawerBlock">
        <h3>Resource Usage & Allocation</h3>
        <div className="resourceUsageGrid">
          <div className={activeService.evidence.resources.cpu.status}>
            <span>CPU usage</span>
            <strong>{compactPercent(activeService.evidence.resources.cpu.usagePercent)}</strong>
            <em>{activeService.evidence.resources.cpu.provisionedCpus === null ? "allocation n/a" : `${activeService.evidence.resources.cpu.provisionedCpus} vCPU allocated`}</em>
          </div>
          <div className={activeService.evidence.resources.memory.status}>
            <span>Memory usage</span>
            <strong>{compactPercent(activeService.evidence.resources.memory.usagePercent)}</strong>
            <em>
              {activeService.evidence.resources.memory.usedMb !== null && activeService.evidence.resources.memory.limitMb !== null
                ? `${compactMb(activeService.evidence.resources.memory.usedMb)} / ${compactMb(activeService.evidence.resources.memory.limitMb)}`
                : activeService.evidence.resources.memory.provisionedMb === null
                  ? "allocation n/a"
                  : `${compactMb(activeService.evidence.resources.memory.provisionedMb)} allocated`}
            </em>
          </div>
          <div>
            <span>Instances</span>
            <strong>
              {activeService.evidence.resources.instances.running === null || activeService.evidence.resources.instances.desired === null
                ? "n/a"
                : `${activeService.evidence.resources.instances.running}/${activeService.evidence.resources.instances.desired}`}
            </strong>
            <em>{resourceSourceLabel(activeService.evidence.resources.source)}</em>
          </div>
        </div>
        <p className="evidenceMessage">
          {activeService.evidence.resources.message}
          {activeService.evidence.resources.checkedAt ? ` Checked ${since(activeService.evidence.resources.checkedAt)}.` : ""}
        </p>
      </div>
      <div className="drawerBlock">
        <h3>Log & Resource Evidence</h3>
        <p className="evidenceMessage">{serviceLogMessage(activeService, props.snapshot)}</p>
        {activeService.evidence.grafana.logEvents.length ? (
          <div className="logEventList compact">
            {activeService.evidence.grafana.logEvents.slice(0, 10).map((event) => (
              <div className={cx("logEventRow", event.severity)} key={event.id}>
                <div className="logEventMeta">
                  <strong>{event.severity}</strong>
                  <time>{since(event.time)}</time>
                  <span>{labelText(event.labels) || event.source}</span>
                </div>
                <p>{event.message}</p>
                <LogEventDetails event={event} />
              </div>
            ))}
          </div>
        ) : (
          <p className="mutedCopy">No recent log or resource evidence returned for this service.</p>
        )}
        <DeepLink href={activeService.links.grafana.loki} label="Loki Logs" />
        <DeepLink href={activeService.links.grafana.traces} label="Tempo Traces" />
        <DeepLink href={activeService.links.grafana.metrics} label="Metrics Dashboard" />
        <DeepLink href={activeService.links.grafana.dashboard} label="Grafana Dashboard" />
      </div>
      <div className="drawerBlock">
        <h3>KPN Service VHosts</h3>
        {vhostLinks.length ? (
          <div className="vhostList">
            {vhostLinks.map((link) => (
              <a className="vhostLink" href={link.url} target="_blank" rel="noreferrer" key={link.url}>
                <span>{link.label}</span>
                <em>{link.meta}</em>
                <ExternalLink size={13} />
              </a>
            ))}
          </div>
        ) : (
          <p className="mutedCopy">No KPN service vhost or endpoint probe linked to this service.</p>
        )}
      </div>
      <div className="drawerBlock">
        <h3>Service Routes</h3>
        {routeDefinitions.length ? (
          <div className="pathDefinitionList">
            {routeDefinitions.map((pathDefinition) => {
              const href = servicePathDefinitionHref(pathDefinition, dshVhostLinks);
              const content = (
                <>
                  <code>{pathDefinition.value}</code>
                  <em>{pathDefinition.evidence === "dsh" ? "DSH service config" : "Live endpoint probe"} - {pathDefinition.source}</em>
                </>
              );
              return href ? (
                <a className="pathDefinitionRow" href={href} target="_blank" rel="noreferrer" key={`${pathDefinition.evidence}:${pathDefinition.source}:${pathDefinition.value}`}>
                  {content}
                  <ExternalLink size={13} />
                </a>
              ) : (
                <div className="pathDefinitionRow" key={`${pathDefinition.evidence}:${pathDefinition.source}:${pathDefinition.value}`}>
                  {content}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mutedCopy">No DSH service route paths or catalog endpoint probe paths are linked to this service.</p>
        )}
      </div>
      <div className="drawerBlock">
        <h3>Endpoint Probes</h3>
        {activeService.evidence.endpointProbes.length ? (
          activeService.evidence.endpointProbes.map((probe) => (
            <div className="certificateLine" key={probe.id}>
              <span>{probe.name}</span>
              <strong className={probe.status === "critical" ? "critical" : probe.status === "warning" ? "warning" : "healthy"}>{probe.status}</strong>
              <em>{probe.statusCode ?? "n/a"} / {probe.latencyMs}ms</em>
            </div>
          ))
        ) : (
          <p className="mutedCopy">No endpoint probe linked to this service.</p>
        )}
      </div>
      <div className="drawerBlock">
        <h3>Certificate</h3>
        {cert ? (
          <div className="certificateLine">
            <span>{cert.name}</span>
            <strong className={cert.status}>{cert.daysRemaining === null ? "unknown" : `${cert.daysRemaining} days`}</strong>
            <em>{cert.expiresOn || "pending"}</em>
          </div>
        ) : (
          <p className="mutedCopy">No certificate check linked to this service and environment.</p>
        )}
      </div>
      <div className="drawerBlock">
        <h3>DSH Services</h3>
        <DeepLink href={activeService.links.dsh.console} label="DSH Console" />
        <DeepLink href={activeService.links.dsh.api} label="DSH Read-only API" />
        <DeepLink href={activeService.runbook} label="Runbook" />
      </div>
    </aside>
  );
}

function SlackTimeline({ snapshot, canRunActions, onSend }: { snapshot: Snapshot; canRunActions: boolean; onSend: (incident: Incident) => void }) {
  const paused = snapshot.integrationHealth.slack.paused;
  return (
    <section className="panel slackPanel">
      <div className="panelHeader">
        <h2>Slack Alert Evidence</h2>
        <span>{paused ? "paused" : snapshot.channels.ops}</span>
      </div>
      <div className="timeline">
        {!snapshot.notifications.length && <p className="mutedCopy">No Slack alert sends recorded in this app runtime.</p>}
        {snapshot.notifications.map((item) => (
          <div key={item.id} className="timelineItem">
            <MessageSquare size={19} />
            <time>{time(item.time)}</time>
            <PriorityBadge priority={item.priority} />
            <p>{item.text}</p>
          </div>
        ))}
      </div>
      {snapshot.incidents[0] && (
        <button
          className="secondaryButton"
          disabled={!canRunActions || paused}
          title={paused ? "Slack alerts are paused" : canRunActions ? "Send to Slack" : "Admin role required"}
          onClick={() => onSend(snapshot.incidents[0])}
        >
          <Send size={15} />
          {paused ? "Slack alerts paused" : "Send top incident to Slack"}
        </button>
      )}
    </section>
  );
}

function ReportReadiness(props: { snapshot: Snapshot; canRunActions: boolean; onReport: () => void; onDownload: () => void; onEmail: () => void; reportMonth: string }) {
  const { snapshot } = props;
  return (
    <section className="panel reportPanel">
      <div className="panelHeader">
        <h2>{snapshot.contract?.serviceName || "Managed Service Availability Report"}</h2>
        <a href={snapshot.standards.ishareOpenApi.url} target="_blank" rel="noreferrer">
          iSHARE OpenAPI <ExternalLink size={13} />
        </a>
      </div>
      <div className="checklist">
        {snapshot.reportChecklist.map((item) => (
          <div key={item.section} className={cx("checkRow", item.status)}>
            <span>{item.section}</span>
            <strong>{item.status}</strong>
            <em>{item.details}</em>
          </div>
        ))}
      </div>
      <div className="reportActions">
        <span>{props.reportMonth}</span>
        <button className="secondaryButton" onClick={props.onReport}>
          <FileText size={16} />
          Preview
        </button>
        <button className="primaryButton" onClick={props.onDownload}>
          <Download size={16} />
          Download PDF report
        </button>
        <button className="secondaryButton" disabled={!props.canRunActions} title={props.canRunActions ? "Email report" : "Admin role required"} onClick={props.onEmail}>
          <Send size={16} />
          Email report
        </button>
      </div>
    </section>
  );
}

function ViewHeader(props: { icon: ReactNode; title: string; description: string; meta?: string; children?: ReactNode }) {
  return (
    <section className="viewHeader">
      <div className="viewHeaderIcon">{props.icon}</div>
      <div>
        <span>{props.meta}</span>
        <h2>{props.title}</h2>
        <p>{props.description}</p>
      </div>
      {props.children && <div className="viewHeaderActions">{props.children}</div>}
    </section>
  );
}

function MetricBand({ items }: { items: Array<{ label: string; value: string | number; tone?: "good" | "warn" | "bad" | "neutral" }> }) {
  return (
    <section className="metricBand" aria-label="View metrics">
      {items.map((item) => (
        <div key={item.label} className={cx("metricTile", item.tone || "neutral")}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </section>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="emptyState">
      <CheckCircle2 size={22} />
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function RowLink({ href, label }: { href?: string | null; label: string }) {
  if (!href) return <span className="mutedCopy">Pending</span>;
  return (
    <a className="rowLink" href={href} target="_blank" rel="noreferrer">
      {label}
      <ExternalLink size={13} />
    </a>
  );
}

function EnvironmentView(props: {
  snapshot: Snapshot;
  environments: EnvironmentSummary[];
  services: ServiceRow[];
  onSelectService: (service: ServiceRow) => void;
  onOpenServices: () => void;
}) {
  const dshByEnv = new Map(props.snapshot.integrationHealth.dsh.environments.map((environment) => [environment.id, environment]));
  const grafanaByEnv = new Map(props.snapshot.integrationHealth.grafana.environments.map((environment) => [environment.id, environment]));
  return (
    <div className="viewStack">
      <ViewHeader
        icon={<Layers3 size={22} />}
        meta="OTAP environments"
        title="Environment Control"
        description="POC, ACC and PROD health, DSH read coverage, log evidence and SLA position from the current live snapshot."
      />
      <MetricBand
        items={[
          { label: "Environments", value: props.environments.length },
          { label: "Live DSH reads", value: props.snapshot.metrics.liveDshChecks, tone: props.snapshot.metrics.liveDshChecks ? "good" : "warn" },
          { label: "Log source", value: logSourceLabel(props.snapshot), tone: logSourceTone(props.snapshot) },
          { label: "Endpoint failures", value: props.snapshot.metrics.endpointProbeFailures, tone: props.snapshot.metrics.endpointProbeFailures ? "bad" : "good" }
        ]}
      />
      <section className="environmentGrid">
        {props.environments.map((environment) => {
          const dsh = dshByEnv.get(environment.id);
          const grafana = grafanaByEnv.get(environment.id);
          const logStatus = logBridgeReady(props.snapshot)
            ? titleCase(props.snapshot.integrationHealth.logIngest.status)
            : grafana
              ? titleCase(grafana.status)
              : "Pending";
          const logMeta = logBridgeReady(props.snapshot)
            ? `${props.snapshot.integrationHealth.logIngest.eventCount} retained events`
            : grafana
              ? `${grafana.connected}/${grafana.serviceCount} checks`
              : "not configured";
          const services = props.services.filter((service) => service.environment === environment.id);
          const issues = props.snapshot.incidents.filter((incident) => incident.environment === environment.id);
          return (
            <article key={environment.id} className={cx("environmentCard", environment.status)}>
              <div className="environmentCardHeader">
                <div>
                  <span>{environment.purpose}</span>
                  <h3>{environment.label}</h3>
                </div>
                <StatusBadge status={environment.status} />
              </div>
              <div className="environmentStats">
                <div><span>SLA target</span><strong>{environment.slaTarget}%</strong></div>
                <div><span>Availability</span><strong>{percentValue(environment.uptime30d)}</strong></div>
                <div><span>Services</span><strong>{environment.serviceCount}</strong></div>
                <div><span>Incidents</span><strong className={issues.length ? "warn" : "good"}>{issues.length}</strong></div>
              </div>
              <div className="environmentSourceRows">
                <div><span>DSH</span><strong>{dsh ? titleCase(dsh.status) : "Pending"}</strong><em>{dsh ? `${dsh.connected}/${dsh.serviceCount} connected` : "not configured"}</em></div>
                <div><span>Logs</span><strong>{logStatus}</strong><em>{logMeta}</em></div>
              </div>
              <div className="environmentServiceList">
                {services.slice(0, 5).map((service) => (
                  <button key={service.id} onClick={() => props.onSelectService(service)}>
                    <span>{service.serviceName}</span>
                    <StatusBadge status={service.status} />
                  </button>
                ))}
                {!services.length && <span className="mutedCopy">No matching services for the current filter.</span>}
              </div>
              <button className="secondaryButton" onClick={props.onOpenServices}>
                <Box size={15} />
                Open service matrix
              </button>
            </article>
          );
        })}
      </section>
    </div>
  );
}

function ServicesView(props: { snapshot: Snapshot; services: ServiceRow[]; selectedEnv: string; selectedServiceId?: string; onSelect: (service: ServiceRow) => void }) {
  const criticalServices = props.services.filter((service) => service.status === "down" || service.status === "degraded").length;
  return (
    <div className="viewStack">
      <ViewHeader
        icon={<Box size={22} />}
        meta="DSH service matrix"
        title="Services"
        description="Read-only stoplight matrix with live DSH evidence, log source and endpoint probes per OTAP environment."
      />
      <MetricBand
        items={[
          { label: "Visible services", value: props.services.length },
          { label: "Attention needed", value: criticalServices, tone: criticalServices ? "warn" : "good" },
          { label: "Selected scope", value: props.selectedEnv.toUpperCase() }
        ]}
      />
      <MobileServiceIssueCards services={props.services} snapshot={props.snapshot} onSelect={props.onSelect} />
      <ServicesMatrix services={props.services} snapshot={props.snapshot} selectedEnv={props.selectedEnv} selectedServiceId={props.selectedServiceId} onSelect={props.onSelect} />
    </div>
  );
}

function IncidentsView(props: {
  incidents: Incident[];
  activeId?: string;
  canRunActions: boolean;
  slackPaused: boolean;
  onSelect: (incident: Incident) => void;
  onSend: (incident: Incident) => void;
}) {
  const critical = props.incidents.filter((incident) => incident.priority === "critical").length;
  const high = props.incidents.filter((incident) => incident.priority === "high").length;
  return (
    <div className="viewStack">
      <ViewHeader
        icon={<Siren size={22} />}
        meta="Incident response"
        title="Incidents"
        description="Prioritised queue for SLA risk, server errors, endpoint failures, certificate risk and version drift."
      />
      <MetricBand
        items={[
          { label: "Open incidents", value: props.incidents.length, tone: props.incidents.length ? "warn" : "good" },
          { label: "Critical", value: critical, tone: critical ? "bad" : "good" },
          { label: "High", value: high, tone: high ? "warn" : "good" },
          { label: "Slack ready", value: props.slackPaused ? "Paused" : "Manual + auto", tone: props.slackPaused ? "warn" : "good" }
        ]}
      />
      {!props.incidents.length ? (
        <EmptyState title="No incidents in scope" body="The current environment and search filters do not contain active incidents." />
      ) : (
        <section className="panel tablePanel">
          <div className="panelHeader">
            <h2>Incident Queue</h2>
            <span>{props.incidents.length} items</span>
          </div>
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Incident</th>
                  <th>Service</th>
                  <th>Env</th>
                  <th>Since</th>
                  <th>Metric</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {props.incidents.map((incident) => (
                  <tr
                    key={incident.id}
                    id={incidentElementId(incident.id)}
                    tabIndex={-1}
                    className={props.activeId === incident.id ? "selectedRow" : ""}
                  >
                    <td><PriorityBadge priority={incident.priority} /></td>
                    <td>
                      <strong>{incident.title}</strong>
                      <span>{incident.recommendedAction}</span>
                    </td>
                    <td>{incident.serviceName}</td>
                    <td>{incident.environmentLabel}</td>
                    <td>{since(incident.since)}</td>
                    <td>{incident.metric}</td>
                    <td>
                      <div className="rowActions">
                        <button className="rowAction" onClick={() => props.onSelect(incident)}>Inspect</button>
                        <button
                          className="rowAction"
                          disabled={!props.canRunActions || props.slackPaused}
                          title={props.slackPaused ? "Slack alerts are paused" : props.canRunActions ? "Send to Slack" : "Admin role required"}
                          onClick={() => props.onSend(incident)}
                        >
                          Slack
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function LogsView(props: { snapshot: Snapshot; services: ServiceRow[]; endpointProbes: EndpointProbeRow[] }) {
  const servicesWithErrors = props.services.filter((service) => (service.grafanaErrorCount || 0) > 0).length;
  const logEvents = props.services
    .flatMap((service) => service.evidence.grafana.logEvents.map((event) => ({ service, event })))
    .sort((left, right) => Date.parse(right.event.time) - Date.parse(left.event.time));
  const bridgeReady = logBridgeReady(props.snapshot);
  return (
    <div className="viewStack">
      <ViewHeader
        icon={<TerminalSquare size={22} />}
        meta={logSourceLabel(props.snapshot)}
        title="Logs"
        description="Log source status by environment, service error counts and direct links into the relevant dashboards."
      />
      <MetricBand
        items={[
          { label: "Log source", value: logSourceLabel(props.snapshot), tone: logSourceTone(props.snapshot) },
          { label: "Bridge events", value: props.snapshot.metrics.forwardedLogEvents, tone: props.snapshot.metrics.forwardedLogEvents ? "warn" : "good" },
          { label: "Services with errors", value: servicesWithErrors, tone: servicesWithErrors ? "warn" : "good" },
          { label: "Evidence rows", value: logEvents.length, tone: logEvents.length ? "warn" : "good" },
          { label: "Endpoint probes", value: props.endpointProbes.length },
        ]}
      />
      <section className="statusGrid">
        {!bridgeReady && props.snapshot.integrationHealth.grafana.environments.map((environment) => (
          <article key={environment.id} className={cx("statusCard", environment.status)}>
            <span>{environment.label}</span>
            <strong>{titleCase(environment.status)}</strong>
            <p>{environment.connected}/{environment.serviceCount} service log checks, {environment.errors} errors.</p>
            <em>{environment.missing.length ? environment.missing.join(", ") : titleCase(environment.mode)}</em>
          </article>
        ))}
        {props.snapshot.integrationHealth.logIngest.environments.map((environment) => (
          <article className="statusCard" key={`bridge-${environment.id}`}>
            <span>{environment.label} bridge</span>
            <strong>{titleCase(environment.status)}</strong>
            <p>
              {environment.serviceCount ?? "n/a"} services, {environment.events} retained event(s)
              {environment.lastPollAt ? `, poll ${since(environment.lastPollAt)}` : environment.latestEventAt ? `, latest ${since(environment.latestEventAt)}` : ""}
            </p>
            {environment.queryFailures ? <em>{environment.queryFailures} query failure(s)</em> : null}
          </article>
        ))}
      </section>
      <section className="panel logDetailsPanel">
        <div className="panelHeader">
          <h2>Recent Evidence Details</h2>
          <span>{logEvents.length} rows</span>
        </div>
        {logEvents.length ? (
          <div className="logEventList">
            {logEvents.slice(0, 30).map(({ service, event }) => (
              <div className={cx("logEventRow", event.severity)} key={`${service.id}:${event.id}`}>
                <div className="logEventMeta">
                  <strong>{event.severity}</strong>
                  <time>{since(event.time)}</time>
                  <span>{service.environmentLabel}</span>
                  <span>{service.serviceName}</span>
                  <span>{labelText(event.labels) || event.source}</span>
                </div>
                <p>{event.message}</p>
                <LogEventDetails event={event} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No evidence rows in scope" body="The active log and metric sources returned no detailed rows for the current environment and search filters." />
        )}
      </section>
      <section className="panel tablePanel">
        <div className="panelHeader">
          <h2>Service Evidence</h2>
          <span>{props.services.length} rows</span>
        </div>
        <div className="tableWrap">
          <table className="dataTable">
            <thead>
              <tr>
                <th>Service</th>
                <th>Env</th>
                <th>Log evidence</th>
                <th>Errors</th>
                <th>Latest signal</th>
                <th>Links</th>
              </tr>
            </thead>
            <tbody>
              {props.services.map((service) => {
                const logEvidence = serviceLogEvidence(service, props.snapshot);
                return (
                  <tr key={service.id}>
                    <td>
                      <strong>{service.serviceName}</strong>
                      <span>{service.dshServiceName}</span>
                    </td>
                    <td>{service.environmentLabel}</td>
                    <td className={logEvidence.tone}>
                      <strong>{logEvidence.label}</strong>
                      <span>{logEvidence.source}</span>
                    </td>
                    <td className={(service.grafanaErrorCount || 0) > 0 ? "bad" : "good"}>{formatValue(service.grafanaErrorCount)}</td>
                    <td>
                      <span>{service.evidence.grafana.logEvents[0]?.message || service.evidence.grafana.sample || serviceLogMessage(service, props.snapshot)}</span>
                    </td>
                    <td>
                      <div className="rowActions">
                        <RowLink href={service.links.grafana.loki} label="Logs" />
                        <RowLink href={service.links.grafana.dashboard} label="Dashboard" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function CertificatesView(props: { certificates: CertificateRow[]; services: ServiceRow[]; onSelectService: (service: ServiceRow) => void }) {
  const score = { critical: 0, warning: 1, unknown: 2, healthy: 3 };
  const rows = [...props.certificates].sort((a, b) => score[a.status] - score[b.status] || (a.daysRemaining ?? 9999) - (b.daysRemaining ?? 9999));
  const critical = rows.filter((certificate) => certificate.status === "critical").length;
  const warning = rows.filter((certificate) => certificate.status === "warning").length;
  return (
    <div className="viewStack">
      <ViewHeader
        icon={<Shield size={22} />}
        meta="Certificate expiry"
        title="Certificates"
        description="TLS expiry checks for DSH service endpoints, sorted by action priority."
      />
      <MetricBand
        items={[
          { label: "Certificates", value: rows.length },
          { label: "Critical", value: critical, tone: critical ? "bad" : "good" },
          { label: "Warning", value: warning, tone: warning ? "warn" : "good" },
          { label: "Healthy", value: rows.filter((certificate) => certificate.status === "healthy").length, tone: "good" }
        ]}
      />
      {!rows.length ? (
        <EmptyState title="No certificates in scope" body="There are no certificate rows for the current filter." />
      ) : (
        <section className="panel tablePanel">
          <div className="panelHeader">
            <h2>Certificate Register</h2>
            <span>{rows.length} checks</span>
          </div>
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Env</th>
                  <th>Host</th>
                  <th>Status</th>
                  <th>Remaining</th>
                  <th>Expires</th>
                  <th>Service</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((certificate) => {
                  const service = props.services.find((row) => row.serviceId === certificate.serviceId && row.environment === certificate.environment);
                  return (
                    <tr key={certificate.id}>
                      <td>
                        <strong>{certificate.name}</strong>
                        {certificate.liveError && <span>{certificate.liveError}</span>}
                      </td>
                      <td>{certificate.environmentLabel}</td>
                      <td>{certificate.host}</td>
                      <td><span className={cx("certStatus", certificate.status)}>{certificate.status}</span></td>
                      <td>{certificate.daysRemaining === null ? "pending" : `${certificate.daysRemaining} days`}</td>
                      <td>{formatDate(certificate.expiresOn)}</td>
                      <td>
                        {service ? (
                          <button className="rowAction" onClick={() => props.onSelectService(service)}>Inspect</button>
                        ) : (
                          <span className="mutedCopy">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function ReportsView(props: {
  snapshot: Snapshot;
  canRunActions: boolean;
  onReport: () => void;
  onDownload: () => void;
  onReportMonth: (month: string) => void;
  onDownloadMonth: (month: string) => void;
  onEmail: () => void;
  reportMonth: string;
  setReportMonth: (month: string) => void;
}) {
  const contract = props.snapshot.contract;
  const months = recentReportMonths(18);
  return (
    <div className="viewStack">
      <ViewHeader
        icon={<FileText size={22} />}
        meta={contract?.serviceName || "Managed Service Availability Report"}
        title="Reports"
        description="Weekly or monthly SLA reporting package with availability, incidents, service evidence, certificates and OTAP checklist status. Version drift is shown only when release metadata is available."
      >
        <button className="primaryButton" onClick={props.onDownload}>
          <Download size={16} />
          Download PDF
        </button>
      </ViewHeader>
      <MetricBand
        items={[
          { label: "SLA target", value: `${contract?.sla.availabilityTarget ?? 99.5}%` },
          { label: "Portfolios", value: props.snapshot.metrics.portfolios },
          { label: "Report rows", value: props.snapshot.reportChecklist.length },
          { label: "Open incidents", value: props.snapshot.metrics.openIncidents, tone: props.snapshot.metrics.openIncidents ? "warn" : "good" },
          { label: "Critical incidents", value: props.snapshot.metrics.criticalIncidents, tone: props.snapshot.metrics.criticalIncidents ? "bad" : "good" }
        ]}
      />
      <ReportReadiness
        snapshot={props.snapshot}
        canRunActions={props.canRunActions}
        onReport={props.onReport}
        onDownload={props.onDownload}
        onEmail={props.onEmail}
        reportMonth={props.reportMonth}
      />
      <section className="panel reportArchivePanel">
        <div className="panelHeader">
          <h2>Monthly Report Archive</h2>
          <span>Download any monthly SLA report</span>
        </div>
        <div className="archiveControls">
          <label className="monthBox light" title="Select report month">
            <CalendarDays size={16} />
            <input type="month" value={props.reportMonth} onChange={(event) => props.setReportMonth(event.target.value)} />
          </label>
          <button className="secondaryButton" onClick={props.onReport}>
            <FileText size={16} />
            Preview {monthLabel(props.reportMonth)}
          </button>
          <button className="primaryButton" onClick={props.onDownload}>
            <Download size={16} />
            Download {monthLabel(props.reportMonth)}
          </button>
        </div>
        <div className="archiveGrid">
          {months.map((month) => (
            <article key={month} className={cx("archiveMonth", month === props.reportMonth && "selected")}>
              <button className="archiveMonthTitle" onClick={() => props.setReportMonth(month)}>
                <strong>{monthLabel(month)}</strong>
                <span>{month}</span>
              </button>
              <div className="rowActions">
                <button className="rowAction" onClick={() => props.onReportMonth(month)}>Preview</button>
                <button className="rowAction" onClick={() => props.onDownloadMonth(month)}>PDF</button>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="panel portfolioPanel">
        <div className="panelHeader">
          <h2>Managed Service Portfolio Coverage</h2>
          <span>{props.snapshot.portfolios.length} monitored portfolios</span>
        </div>
        <div className="portfolioGrid">
          {props.snapshot.portfolios.map((portfolio) => (
            <article key={portfolio.id} className={cx("portfolioCard", portfolio.status)}>
              <div>
                <span>{portfolio.customer} / {portfolio.segment}</span>
                <h3>{portfolio.name}</h3>
              </div>
              <strong>{titleCase(portfolio.status)}</strong>
              <p>{portfolio.description}</p>
              <div className="portfolioStats">
                <span>{portfolio.serviceChecks} checks</span>
                <span>{portfolio.environmentCount} envs</span>
                <span>{portfolio.incidents} incidents</span>
              </div>
              <em>{portfolio.reportingCadence}</em>
              {portfolio.plannedServices.length ? <small>{portfolio.plannedServices.join(", ")}</small> : null}
            </article>
          ))}
        </div>
      </section>
      <section className="panel reportDetailPanel">
        <div className="panelHeader">
          <h2>Contract and SLA Context</h2>
          <span>{contract?.contractNumber || "Contract metadata pending"}</span>
        </div>
        <div className="reportDetailGrid">
          <div>
            <span>Service</span>
            <strong>{contract?.serviceName || "KPN FIAM"}</strong>
          </div>
          <div>
            <span>Scope</span>
            <strong>{contract?.sla.scope || "Availability of the FIAM service"}</strong>
          </div>
          <div>
            <span>Window</span>
            <strong>{contract?.sla.measurementWindow || "Monthly"}</strong>
          </div>
          <div>
            <span>Maintenance</span>
            <strong>{contract?.sla.plannedMaintenanceNotice || "Documented in report"}</strong>
          </div>
        </div>
        {contract?.sourceDocuments.length ? (
          <div className="sourceDocs">
            {contract.sourceDocuments.map((document) => <span key={document}>{document}</span>)}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SecurityStatusBadge(props: { status: SecurityEvidenceStatus }) {
  return <span className={cx("securityStatusBadge", props.status)}>{titleCase(props.status)}</span>;
}

function SecurityView(props: { session: SessionInfo | null; onToast: (message: string) => void }) {
  const [evidence, setEvidence] = useState<SecurityEvidence | null>(null);
  const [loading, setLoading] = useState(false);
  const canManageSecurity = Boolean(props.session?.permissions.canManageUsers);

  async function loadEvidence(refresh = false) {
    if (!canManageSecurity) return;
    setLoading(true);
    try {
      setEvidence(await fetchSecurityEvidence(refresh));
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Security evidence request failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadEvidence(false);
  }, [canManageSecurity]);

  if (!canManageSecurity) {
    return (
      <div className="viewStack">
        <ViewHeader
          icon={<Lock size={22} />}
          meta="Admin only"
          title="Security Evidence"
          description="ISO27001 evidence is restricted to portal admins."
        />
        <EmptyState title="Admin access required" body="Security evidence is not exposed to customer or read-only accounts." />
      </div>
    );
  }

  if (!evidence) {
    return (
      <div className="viewStack">
        <ViewHeader
          icon={<ShieldCheck size={22} />}
          meta="ISO27001 evidence"
          title="Security Evidence"
          description="Continuous admin-only evidence for access, monitoring, alerting, certificates, continuity and reporting controls."
        >
          <button className="secondaryButton" onClick={() => loadEvidence(true)} disabled={loading}>
            <RefreshCw size={16} className={loading ? "spin" : ""} />
            Refresh
          </button>
        </ViewHeader>
        <EmptyState title={loading ? "Loading security evidence" : "No security evidence loaded"} body="The portal is collecting the current admin evidence snapshot." />
      </div>
    );
  }

  const priorityFindings = evidence.findings.filter((finding) => finding.status === "critical" || finding.status === "warning").slice(0, 8);

  return (
    <div className="viewStack">
      <ViewHeader
        icon={<ShieldCheck size={22} />}
        meta="ISO27001 continuous evidence"
        title="Security Evidence"
        description="Admin-only control evidence mapped to ISO27001/27002 control areas. Pending items are deliberate evidence gaps, not greenwashed controls."
      >
        <button className="secondaryButton" onClick={() => loadEvidence(true)} disabled={loading}>
          <RefreshCw size={16} className={loading ? "spin" : ""} />
          Refresh evidence
        </button>
      </ViewHeader>
      <MetricBand
        items={[
          { label: "Overall status", value: titleCase(evidence.overallStatus), tone: securityStatusTone(evidence.overallStatus) },
          { label: "Controls", value: evidence.metrics.controls },
          { label: "Critical", value: evidence.metrics.critical, tone: evidence.metrics.critical ? "bad" : "good" },
          { label: "Warnings", value: evidence.metrics.warning, tone: evidence.metrics.warning ? "warn" : "good" },
          { label: "Pending", value: evidence.metrics.pending, tone: evidence.metrics.pending ? "warn" : "good" },
          { label: "Passkeys", value: `${evidence.metrics.passkeyCoverage}%`, tone: evidence.metrics.passkeyCoverage === 100 ? "good" : "warn" }
        ]}
      />
      <section className="panel securityEvidencePanel">
        <div className="panelHeader">
          <h2>Evidence Scope</h2>
          <span>Generated {since(evidence.generatedAt)}</span>
        </div>
        <div className="securityEvidenceIntro">
          <p>{evidence.scope}</p>
          <div>
            <span>Active users</span>
            <strong>{evidence.userEvidence.activeUsers}</strong>
          </div>
          <div>
            <span>Admins</span>
            <strong>{evidence.userEvidence.admins.length}</strong>
          </div>
          <div>
            <span>Critical email users</span>
            <strong>{evidence.userEvidence.usersWithCriticalEmail.length}</strong>
          </div>
          <div>
            <span>Phone subscriptions</span>
            <strong>{evidence.userEvidence.pushSubscriptions}</strong>
          </div>
        </div>
      </section>
      <section className="panel securityControlsPanel">
        <div className="panelHeader">
          <h2>Control Evidence</h2>
          <span>{evidence.metrics.complete}/{evidence.metrics.controls} complete</span>
        </div>
        <div className="securityControlGrid">
          {evidence.controls.map((control) => (
            <article key={control.id} className={cx("securityControlCard", control.status)}>
              <div className="securityControlHeader">
                <div>
                  <span>{control.iso} · {control.domain}</span>
                  <h3>{control.title}</h3>
                </div>
                <SecurityStatusBadge status={control.status} />
              </div>
              <p>{control.summary}</p>
              <dl>
                {control.evidence.slice(0, 5).map((item) => (
                  <div key={`${control.id}-${item.label}`}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
              <em>{control.recommendation}</em>
            </article>
          ))}
        </div>
      </section>
      <div className="securityBottomGrid">
        <section className="panel">
          <div className="panelHeader">
            <h2>Priority Findings</h2>
            <span>{evidence.findings.length} open</span>
          </div>
          {priorityFindings.length ? (
            <div className="securityFindingList">
              {priorityFindings.map((finding) => (
                <div key={finding.id} className={cx("securityFinding", finding.status)}>
                  <SecurityStatusBadge status={finding.status} />
                  <div>
                    <strong>{finding.title}</strong>
                    <span>{finding.iso}</span>
                    <p>{finding.summary}</p>
                    <em>{finding.recommendation}</em>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No critical or warning findings" body="Pending evidence gaps may still require manual audit follow-up." />
          )}
        </section>
        <section className="panel">
          <div className="panelHeader">
            <h2>Evidence Sources</h2>
            <span>{evidence.evidenceSources.length} sources</span>
          </div>
          <div className="securitySourceList">
            {evidence.evidenceSources.map((source) => (
              <div key={source.name} className="securitySourceRow">
                <SecurityStatusBadge status={source.status} />
                <strong>{source.name}</strong>
                <span>{source.details || (source.checkedAt ? since(source.checkedAt) : "pending")}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function shortCredentialId(value: string) {
  if (!value) return "unknown";
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}...${value.slice(-6)}`;
}

function AccountSecurityPanel(props: { session: SessionInfo | null; onToast: (message: string) => void }) {
  const [passkeys, setPasskeys] = useState<PortalPasskey[]>([]);
  const [store, setStore] = useState<SessionInfo["passkeyStore"] | null>(null);
  const [supportMessage, setSupportMessage] = useState<string | null>(null);
  const [email, setEmail] = useState(props.session?.user?.email || "");
  const [emailAlerts, setEmailAlerts] = useState(Boolean(props.session?.user?.emailAlerts));
  const [pushStatus, setPushStatus] = useState<PushSubscriptionStatus | null>(null);
  const [pushSupportMessage, setPushSupportMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  async function loadPasskeys() {
    try {
      const support = await getPasskeySupport();
      setSupportMessage(support.available ? null : support.reason || "Passkeys are not available in this browser.");
      const result = await fetchPasskeys();
      setPasskeys(result.passkeys);
      setStore(result.store);
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Passkey list failed");
    }
  }

  async function loadPushStatus() {
    try {
      const [support, status] = await Promise.all([getPushSupport(), fetchPushStatus()]);
      setPushSupportMessage(support.available ? null : support.reason || "Phone notifications are not available in this browser.");
      setPushStatus(status);
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Push status failed");
    }
  }

  useEffect(() => {
    if (props.session?.user) {
      void loadPasskeys();
      void loadPushStatus();
    }
  }, [props.session?.user?.username]);

  useEffect(() => {
    setEmail(props.session?.user?.email || "");
    setEmailAlerts(Boolean(props.session?.user?.emailAlerts));
  }, [props.session?.user?.username, props.session?.user?.email, props.session?.user?.emailAlerts]);

  async function handleEmailSave(event: FormEvent) {
    event.preventDefault();
    setEmailBusy(true);
    try {
      const result = await updateAccount({ email, emailAlerts });
      setEmail(result.user.email || "");
      setEmailAlerts(Boolean(result.user.emailAlerts));
      props.onToast("Email preferences updated");
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Email update failed");
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleRegisterPasskey() {
    setBusy(true);
    try {
      const support = await getPasskeySupport();
      if (!support.available) {
        throw new Error(support.reason || "Passkeys are not available in this browser.");
      }
      const { options } = await passkeyRegistrationOptions();
      const credential = await createPasskeyAttestation(options);
      await verifyPasskeyRegistration(credential);
      props.onToast("Passkey activated");
      await loadPasskeys();
    } catch (error) {
      props.onToast(getPasskeyErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeletePasskey(passkey: PortalPasskey) {
    setBusy(true);
    try {
      await deletePasskey(passkey.credentialId);
      props.onToast("Passkey removed");
      await loadPasskeys();
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Passkey removal failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleEnablePush() {
    setPushBusy(true);
    try {
      const status = pushStatus || await fetchPushStatus();
      if (!status.configured) {
        throw new Error(`Phone notifications are not configured: ${status.missing.join(", ")}`);
      }
      const subscription = await subscribeToPush(status.publicKey);
      const result = await savePushSubscription(subscription.toJSON());
      setPushStatus(result.status);
      props.onToast("Phone notifications enabled");
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Phone notification setup failed");
    } finally {
      setPushBusy(false);
    }
  }

  async function handleDisablePush() {
    setPushBusy(true);
    try {
      const result = await unsubscribeFromPush();
      const response = await deletePushSubscription(result.endpoint);
      setPushStatus(response.status);
      props.onToast("Phone notifications disabled");
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Phone notification removal failed");
    } finally {
      setPushBusy(false);
    }
  }

  const phoneNotificationsEnabled = Boolean(pushStatus?.subscriptionCount);

  return (
    <section className="panel passkeyPanel">
      <div className="panelHeader">
        <div>
          <h2>Account Passkeys</h2>
          <span>{passkeys.length ? `${passkeys.length} registered passkey${passkeys.length === 1 ? "" : "s"}` : "Password first, passkey after sign-in"}</span>
        </div>
        <button className="primaryButton" onClick={handleRegisterPasskey} disabled={busy || Boolean(supportMessage)}>
          <KeyRound size={16} />
          {busy ? "Working" : "Activate passkey"}
        </button>
      </div>
      <p className="mutedCopy">
        Sign in with username and password once, then attach a device passkey to this account for stronger future access.
      </p>
      <form className="accountContactForm" onSubmit={handleEmailSave}>
        <label>
          <span>Notification email</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@company.com"
          />
        </label>
        <label className="accountAlertToggle">
          <input
            type="checkbox"
            checked={emailAlerts}
            onChange={(event) => setEmailAlerts(event.target.checked)}
          />
          <span>Critical email alerts</span>
        </label>
        <button className="secondaryButton" type="submit" disabled={emailBusy}>
          <Mail size={16} />
          {emailBusy ? "Saving" : "Save preferences"}
        </button>
      </form>
      {supportMessage ? <p className="warningText">{supportMessage}</p> : null}
      {store && !store.persistent && (
        <p className="warningText">
          Runtime passkey store is active. Set AUTH_PASSKEY_STORE_DATABASE_URL, AUTH_USER_STORE_DATABASE_URL or AUTH_PASSKEY_STORE_PATH before relying on passkeys across redeploys.
        </p>
      )}
      <div className="pushPanel">
        <div>
          <strong>Phone notifications</strong>
          <span>
            {phoneNotificationsEnabled
              ? `Enabled for ${pushStatus?.environments.join(", ").toUpperCase() || "PROD"} critical alerts`
              : pushStatus?.configured
                ? "Disabled on this account"
                : "Server setup pending"}
          </span>
        </div>
        <button
          className={phoneNotificationsEnabled ? "secondaryButton" : "primaryButton"}
          onClick={phoneNotificationsEnabled ? handleDisablePush : handleEnablePush}
          disabled={pushBusy || Boolean(pushSupportMessage) || (pushStatus ? !pushStatus.configured && !phoneNotificationsEnabled : false)}
        >
          {phoneNotificationsEnabled ? <BellOff size={16} /> : <Bell size={16} />}
          {pushBusy ? "Working" : phoneNotificationsEnabled ? "Disable" : "Enable phone alerts"}
        </button>
      </div>
      {pushSupportMessage ? <p className="warningText">{pushSupportMessage}</p> : null}
      {pushStatus && !pushStatus.configured ? <p className="warningText">Web Push is missing: {pushStatus.missing.join(", ")}</p> : null}
      {pushStatus && !pushStatus.store.persistent ? (
        <p className="warningText">Runtime push store is active. Configure Postgres or WEB_PUSH_STORE_PATH before relying on phone subscriptions across redeploys.</p>
      ) : null}
      <div className="passkeyList">
        {passkeys.map((passkey) => (
          <div key={passkey.credentialId} className="passkeyItem">
            <div>
              <strong>{shortCredentialId(passkey.credentialId)}</strong>
              <span>
                {passkey.credentialDeviceType === "multiDevice" ? "Synced passkey" : "Device passkey"}
                {passkey.lastUsedAt ? ` · Last used ${since(passkey.lastUsedAt)}` : ` · Created ${passkey.createdAt ? since(passkey.createdAt) : "unknown"}`}
              </span>
            </div>
            <button className="rowAction danger" onClick={() => handleDeletePasskey(passkey)} disabled={busy}>
              <Trash2 size={13} />
              Remove
            </button>
          </div>
        ))}
        {!passkeys.length ? <span className="mutedCopy">No passkey is linked to this account yet.</span> : null}
      </div>
    </section>
  );
}

function UsersView(props: { session: SessionInfo | null; onToast: (message: string) => void }) {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [store, setStore] = useState<SessionInfo["userStore"] | null>(null);
  const [accessOptions, setAccessOptions] = useState<AccessOptions>({ portfolios: [], customers: [] });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", emailAlerts: false, password: "", role: "user" as UserRole, portfolioIds: [] as string[] });
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({});

  const canManageUsers = Boolean(props.session?.permissions.canManageUsers);
  const accessLabel = props.session?.access?.mode === "all"
    ? "All customers"
    : props.session?.access?.portfolios.map((portfolio) => portfolio.customer).join(", ") || "No customer scope";

  function togglePortfolio(currentIds: string[], portfolioId: string) {
    return currentIds.includes(portfolioId)
      ? currentIds.filter((id) => id !== portfolioId)
      : [...currentIds, portfolioId].sort();
  }

  function portfolioSummary(portfolioIds: string[]) {
    if (!portfolioIds.length) return "No customer scope";
    const names = portfolioIds.map((id) => accessOptions.portfolios.find((portfolio) => portfolio.id === id)?.customer || id);
    return Array.from(new Set(names)).join(", ");
  }

  async function loadUsers() {
    if (!canManageUsers) return;
    setLoading(true);
    try {
      const result = await fetchUsers();
      setUsers(result.users);
      setEmailDrafts(Object.fromEntries(result.users.map((user) => [user.username, user.email || ""])));
      setStore(result.store);
      setAccessOptions(result.accessOptions);
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "User list failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadUsers();
  }, [canManageUsers]);

  async function handleCreate() {
    try {
      await createUser(form);
      setForm({ username: "", email: "", emailAlerts: false, password: "", role: "user", portfolioIds: [] });
      props.onToast("User created");
      await loadUsers();
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "User creation failed");
    }
  }

  async function handleUpdate(user: PortalUser, changes: { email?: string; emailAlerts?: boolean; role?: UserRole; disabled?: boolean; portfolioIds?: string[] }) {
    try {
      await updateUser(user.username, changes);
      props.onToast("User updated");
      await loadUsers();
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "User update failed");
    }
  }

  async function handlePassword(user: PortalUser) {
    try {
      await updateUser(user.username, { password: passwords[user.username] || "" });
      setPasswords((current) => ({ ...current, [user.username]: "" }));
      props.onToast("Password updated");
      await loadUsers();
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "Password update failed");
    }
  }

  async function handleDelete(user: PortalUser) {
    try {
      await deleteUser(user.username);
      props.onToast("User deleted");
      await loadUsers();
    } catch (error) {
      props.onToast(error instanceof Error ? error.message : "User deletion failed");
    }
  }

  if (!canManageUsers) {
    return (
      <div className="viewStack">
      <ViewHeader
        icon={<Lock size={22} />}
        meta="Access control"
        title="Read-only Access"
        description="Your account can view monitoring evidence, reports and service status, but cannot change portal configuration or send operational notifications."
      />
      <AccountSecurityPanel session={props.session} onToast={props.onToast} />
      <MetricBand
        items={[
            { label: "Signed in as", value: props.session?.user?.username || "unknown" },
            { label: "Role", value: props.session?.user?.role || "user" },
            { label: "Customer access", value: accessLabel, tone: props.session?.access?.portfolios.length ? "good" : "warn" },
            { label: "Admin actions", value: "Disabled", tone: "warn" }
          ]}
        />
        <EmptyState title="Scoped customer access" body="Your account only receives monitoring data for the customer portfolio assigned by a Protium Monitoring admin." />
      </div>
    );
  }

  return (
    <div className="viewStack">
      <ViewHeader
        icon={<UserCog size={22} />}
        meta="Access control"
        title="User Management"
        description="Create named portal users, assign admin or read-only roles, and rotate passwords without changing the DSH monitoring integrations."
      >
        <button className="secondaryButton" onClick={loadUsers}>
          <RefreshCw size={16} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </ViewHeader>
      <AccountSecurityPanel session={props.session} onToast={props.onToast} />
      <MetricBand
        items={[
          { label: "Users", value: users.length },
          { label: "Admins", value: users.filter((user) => user.role === "admin" && !user.disabled).length, tone: "good" },
          { label: "Read-only", value: users.filter((user) => user.role === "user" && !user.disabled).length },
          { label: "Store", value: store?.backend === "postgres" ? "Postgres" : store?.persistent ? "Configured file" : "Runtime file", tone: store?.persistent ? "good" : "warn" }
        ]}
      />
      <section className="panel accessPanel">
        <div className="panelHeader">
          <h2>Create User</h2>
          <span>Minimum 14 character password</span>
        </div>
        <div className="userForm">
          <label>
            <span>Username</span>
            <input value={form.username} onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))} placeholder="kpn.colleague" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} placeholder="name@company.com" />
          </label>
          <div className="toggleFormField">
            <span>Email alerts</span>
            <label className="toggleLine">
              <input
                type="checkbox"
                checked={form.emailAlerts}
                onChange={(event) => setForm((current) => ({ ...current, emailAlerts: event.target.checked }))}
              />
              <span>Critical email</span>
            </label>
          </div>
          <label>
            <span>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              placeholder="At least 14 characters"
            />
          </label>
          <label>
            <span>Role</span>
            <select value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value as UserRole }))}>
              <option value="user">User - read only</option>
              <option value="admin">Admin - manage configuration</option>
            </select>
          </label>
          <div className="scopePicker">
            <span>Customer access</span>
            {accessOptions.portfolios.map((portfolio) => (
              <label key={portfolio.id} className={cx("scopeOption", form.role === "admin" && "disabled")}>
                <input
                  type="checkbox"
                  disabled={form.role === "admin"}
                  checked={form.portfolioIds.includes(portfolio.id)}
                  onChange={() => setForm((current) => ({ ...current, portfolioIds: togglePortfolio(current.portfolioIds, portfolio.id) }))}
                />
                <strong>{portfolio.customer}</strong>
                <small>{portfolio.name}</small>
              </label>
            ))}
            {form.role === "admin" ? <em>Admins can access every customer portfolio.</em> : null}
          </div>
          <button className="primaryButton" onClick={handleCreate}>
            <Plus size={16} />
            Create user
          </button>
        </div>
        {!store?.persistent && (
          <p className="warningText">
            Runtime user store is active. Set AUTH_USER_STORE_DATABASE_URL or AUTH_USER_STORE_PATH to persistent storage before relying on portal-created users across redeploys.
          </p>
        )}
      </section>
      <section className="panel tablePanel">
        <div className="panelHeader">
          <h2>Portal Users</h2>
          <span>{users.length} accounts</span>
        </div>
        <div className="tableWrap">
          <table className="dataTable">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Email alerts</th>
                <th>Role</th>
                <th>Customer access</th>
                <th>Source</th>
                <th>Status</th>
                <th>Password</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={`${user.source}:${user.username}`}>
                  <td>
                    <strong>{user.username}</strong>
                    <span>{user.updatedAt ? `Updated ${since(user.updatedAt)}` : "Bootstrap account"}</span>
                  </td>
                  <td>
                    <div className="inlineEdit">
                      <input
                        type="email"
                        value={emailDrafts[user.username] ?? user.email ?? ""}
                        onChange={(event) => setEmailDrafts((current) => ({ ...current, [user.username]: event.target.value }))}
                        placeholder="name@company.com"
                      />
                      <button
                        className="rowAction"
                        disabled={(emailDrafts[user.username] ?? "") === (user.email || "")}
                        onClick={() => handleUpdate(user, { email: emailDrafts[user.username] ?? "" })}
                      >
                        Save
                      </button>
                    </div>
                  </td>
                  <td>
                    <label className="toggleLine">
                      <input
                        type="checkbox"
                        checked={Boolean(user.emailAlerts)}
                        onChange={(event) => handleUpdate(user, { emailAlerts: event.target.checked })}
                      />
                      <span>{user.emailAlerts ? "Enabled" : "Off"}</span>
                    </label>
                  </td>
                  <td>
                    {user.managed ? (
                      <select
                        value={user.role}
                        onChange={(event) => {
                          const role = event.target.value as UserRole;
                          const fallbackPortfolioId = accessOptions.portfolios[0]?.id;
                          void handleUpdate(user, {
                            role,
                            portfolioIds: role === "user" && !user.portfolioIds.length && fallbackPortfolioId ? [fallbackPortfolioId] : undefined
                          });
                        }}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="rolePill">{user.role}</span>
                    )}
                  </td>
                  <td>
                    {user.role === "admin" ? (
                      <span className="rolePill">All customers</span>
                    ) : user.managed ? (
                      <div className="scopeList">
                        {accessOptions.portfolios.map((portfolio) => (
                          <label key={portfolio.id} className="toggleLine compact">
                            <input
                              type="checkbox"
                              checked={user.portfolioIds.includes(portfolio.id)}
                              onChange={() => handleUpdate(user, { portfolioIds: togglePortfolio(user.portfolioIds, portfolio.id) })}
                            />
                            <span>{portfolio.customer}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <span>{portfolioSummary(user.portfolioIds)}</span>
                    )}
                  </td>
                  <td>{user.source}</td>
                  <td>
                    {user.managed ? (
                      <label className="toggleLine">
                        <input type="checkbox" checked={!user.disabled} onChange={(event) => handleUpdate(user, { disabled: !event.target.checked })} />
                        <span>{user.disabled ? "Disabled" : "Active"}</span>
                      </label>
                    ) : (
                      <span className="rolePill">Active</span>
                    )}
                  </td>
                  <td>
                    {user.managed ? (
                      <input
                        type="password"
                        value={passwords[user.username] || ""}
                        onChange={(event) => setPasswords((current) => ({ ...current, [user.username]: event.target.value }))}
                        placeholder="New password"
                      />
                    ) : (
                      <span className="mutedCopy">Env managed</span>
                    )}
                  </td>
                  <td>
                    <div className="rowActions">
                      <button className="rowAction" disabled={!user.managed || !passwords[user.username]} onClick={() => handlePassword(user)}>
                        Update password
                      </button>
                      <button className="rowAction danger" disabled={!user.managed} onClick={() => handleDelete(user)}>
                        <Trash2 size={13} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length && (
                <tr>
                  <td colSpan={9}>
                    <span className="mutedCopy">No users loaded.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ReportModal(props: { markdown: string; filename: string; onClose: () => void; onDownload: () => void }) {
  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="reportModal">
        <div className="panelHeader">
          <div>
            <h2>Generated OTAP Report</h2>
            <span>{props.filename}</span>
          </div>
          <div className="modalActions">
            <button className="secondaryButton" onClick={props.onDownload}>
              <Download size={15} />
              Download
            </button>
            <button className="iconButton" onClick={props.onClose} aria-label="Close report">
              <XCircle size={18} />
            </button>
          </div>
        </div>
        <textarea value={props.markdown} readOnly />
      </div>
    </div>
  );
}

function LoginPage(props: { ozoneSsoEnabled: boolean; onLogin: (username: string, password: string) => Promise<void>; onPasskeyLogin: (username: string) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await props.onLogin(username, password);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function handlePasskeyLogin() {
    setPasskeyBusy(true);
    setError(null);
    try {
      await props.onPasskeyLogin(username);
    } catch (loginError) {
      setError(getPasskeyErrorMessage(loginError));
    } finally {
      setPasskeyBusy(false);
    }
  }

  return (
    <main className="loginShell">
      <section className="loginPanel" aria-labelledby="login-title">
        <div className="loginBrand">
          <img src="/protium-logo-white.svg" alt="" />
          <span>Protium</span>
        </div>
        <div>
          <span className="loginKicker">Secure SRE console</span>
          <h1 id="login-title">Welcome to Protium Monitoring</h1>
          <p>Live reliability evidence, incidents and SLA reporting for managed services.</p>
        </div>
        {props.ozoneSsoEnabled ? (
          <a className="primaryButton loginButton ssoLoginButton" href="/auth/ozone/start">
            <Lock size={16} />
            Sign in with Google
          </a>
        ) : null}
        <form className="loginForm" onSubmit={handleSubmit}>
          <label>
            <span>Username</span>
            <input
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>
          <label>
            <span>Password</span>
            <input
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {error ? <div className="loginError" role="alert">{error}</div> : null}
          <button className="primaryButton loginButton" disabled={busy || !username || !password}>
            <Lock size={16} />
            {busy ? "Signing in" : "Sign in"}
          </button>
          <button type="button" className="secondaryButton loginButton" disabled={passkeyBusy || !username} onClick={handlePasskeyLogin}>
            <KeyRound size={16} />
            {passkeyBusy ? "Checking passkey" : "Sign in with passkey"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default function App() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [activeView, setActiveView] = useState<ViewKey>(() => viewFromHash());
  const [deepLinkIncidentId, setDeepLinkIncidentId] = useState(() => incidentIdFromHash());
  const [selectedPortfolioId, setSelectedPortfolioId] = useState("");
  const [selectedEnv, setSelectedEnv] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedIncident, setSelectedIncident] = useState<Incident | undefined>();
  const [selectedService, setSelectedService] = useState<ServiceRow | undefined>();
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [authOptions, setAuthOptions] = useState({ ozoneSsoEnabled: false });
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<{ markdown: string; filename: string } | null>(null);
  const [reportMonth, setReportMonth] = useState(currentMonthValue);
  const [toast, setToast] = useState<string | null>(null);
  const [resetPendingId, setResetPendingId] = useState<string | null>(null);
  const appliedDeepLinkIncidentId = useRef<string | null>(null);

  useEffect(() => {
    const onHashChange = () => {
      setActiveView(viewFromHash());
      const nextIncidentId = incidentIdFromHash();
      setDeepLinkIncidentId(nextIncidentId);
      if (nextIncidentId) {
        appliedDeepLinkIncidentId.current = null;
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const nextHash = activeView === "incidents" && (selectedIncident?.id || deepLinkIncidentId)
      ? incidentHash(selectedIncident?.id || deepLinkIncidentId)
      : `#${activeView}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", nextHash);
    }
  }, [activeView, deepLinkIncidentId, selectedIncident?.id]);

  async function load(refresh = false, { silent = false } = {}) {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const nextSession = await fetchSession();
      if (!nextSession) {
        setSession(null);
        setSnapshot(null);
        setAuthRequired(true);
        setError(null);
        return;
      }
      const next = await fetchSnapshot(refresh);
      setSession(nextSession);
      setSnapshot(next);
      setAuthRequired(false);
    } catch (loadError) {
      if (!silent || !snapshot) {
        setError(loadError instanceof Error ? loadError.message : "Failed to load portal data");
      } else {
        console.error("[portal] background snapshot refresh failed", loadError);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    fetchAuthOptions()
      .then((nextAuthOptions) => setAuthOptions(nextAuthOptions))
      .catch(() => setAuthOptions({ ozoneSsoEnabled: false }));
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void load(true, { silent: true });
    }, SNAPSHOT_AUTO_REFRESH_MS);

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        void load(true, { silent: true });
      }
    };

    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, []);

  const portfolioOptions = useMemo(() => {
    const options = snapshot?.portfolios.map((portfolio) => ({
      id: portfolio.id,
      customer: portfolio.customer || portfolio.name,
      name: portfolio.name
    })) || [];
    return options.sort((left, right) => {
      const leftRank = customerSortRank(left.customer);
      const rightRank = customerSortRank(right.customer);
      return leftRank === rightRank
        ? left.customer.localeCompare(right.customer)
        : leftRank - rightRank;
    });
  }, [snapshot]);

  const activePortfolioId = useMemo(() => {
    if (!portfolioOptions.length) return "";
    return portfolioOptions.some((portfolio) => portfolio.id === selectedPortfolioId)
      ? selectedPortfolioId
      : portfolioOptions[0].id;
  }, [portfolioOptions, selectedPortfolioId]);

  const environmentPortfolio = useMemo(() => {
    return new Map((snapshot?.environments || []).map((environment) => [environment.id, environment.portfolioId]));
  }, [snapshot]);

  const serviceKeysInActivePortfolio = useMemo(() => {
    if (!snapshot || !activePortfolioId) return new Set<string>();
    return new Set(
      snapshot.services
        .filter((service) => service.portfolioId === activePortfolioId)
        .map((service) => `${service.environment}:${service.serviceId}`)
    );
  }, [snapshot, activePortfolioId]);

  useEffect(() => {
    if (!portfolioOptions.length) return;
    if (!portfolioOptions.some((portfolio) => portfolio.id === selectedPortfolioId)) {
      setSelectedPortfolioId(portfolioOptions[0].id);
      setSelectedEnv("all");
    }
  }, [portfolioOptions, selectedPortfolioId]);

  useEffect(() => {
    if (!snapshot || selectedEnv === "all" || !activePortfolioId) return;
    const stillInPortfolio = snapshot.environments.some((environment) => environment.portfolioId === activePortfolioId && environment.id === selectedEnv);
    if (!stillInPortfolio) {
      setSelectedEnv("all");
    }
  }, [activePortfolioId, selectedEnv, snapshot]);

  function handlePortfolioChange(portfolioId: string) {
    setSelectedPortfolioId(portfolioId);
    setSelectedEnv("all");
    setSelectedIncident(undefined);
    setSelectedService(undefined);
  }

  const filteredServices = useMemo(() => {
    if (!snapshot) return [];
    const query = search.trim().toLowerCase();
    return snapshot.services.filter((service) => {
      const portfolioMatch = !activePortfolioId || service.portfolioId === activePortfolioId;
      const envMatch = selectedEnv === "all" || service.environment === selectedEnv;
      const text = `${service.serviceName} ${service.dshServiceName} ${service.product} ${service.portfolioName} ${service.customer} ${service.serviceLine} ${service.status} ${service.evidence.dsh.status} ${service.evidence.grafana.status}`.toLowerCase();
      return portfolioMatch && envMatch && (!query || text.includes(query));
    });
  }, [snapshot, activePortfolioId, selectedEnv, search]);

  const filteredIncidents = useMemo(() => {
    if (!snapshot) return [];
    const query = search.trim().toLowerCase();
    return snapshot.incidents.filter((incident) => {
      const portfolioMatch = incident.environment === "all"
        ? activePortfolioId === "kpn-fiam"
        : environmentPortfolio.get(incident.environment) === activePortfolioId;
      const serviceMatch = !incident.serviceId || ["all", "sla-reporting", "dsh-integration", "grafana-integration", "dsh-log-bridge"].includes(incident.serviceId)
        ? true
        : serviceKeysInActivePortfolio.has(`${incident.environment}:${incident.serviceId}`);
      const envMatch = selectedEnv === "all" || incident.environment === selectedEnv || incident.environment === "all";
      const text = `${incident.title} ${incident.serviceName} ${incident.environmentLabel} ${incident.priority}`.toLowerCase();
      return portfolioMatch && serviceMatch && envMatch && (!query || text.includes(query));
    });
  }, [snapshot, activePortfolioId, environmentPortfolio, serviceKeysInActivePortfolio, selectedEnv, search]);

  const filteredCertificates = useMemo(() => {
    if (!snapshot) return [];
    const query = search.trim().toLowerCase();
    return snapshot.certificates.filter((certificate) => {
      const portfolioMatch = environmentPortfolio.get(certificate.environment) === activePortfolioId;
      const envMatch = selectedEnv === "all" || certificate.environment === selectedEnv;
      const text = `${certificate.name} ${certificate.host} ${certificate.status} ${certificate.environmentLabel}`.toLowerCase();
      return portfolioMatch && envMatch && (!query || text.includes(query));
    });
  }, [snapshot, activePortfolioId, environmentPortfolio, selectedEnv, search]);

  const filteredEndpointProbes = useMemo(() => {
    if (!snapshot) return [];
    const query = search.trim().toLowerCase();
    return snapshot.endpointProbes.filter((probe) => {
      const portfolioMatch = environmentPortfolio.get(probe.environment) === activePortfolioId;
      const envMatch = selectedEnv === "all" || probe.environment === selectedEnv;
      const text = `${probe.name} ${probe.url} ${probe.status} ${probe.message}`.toLowerCase();
      return portfolioMatch && envMatch && (!query || text.includes(query));
    });
  }, [snapshot, activePortfolioId, environmentPortfolio, selectedEnv, search]);

  useEffect(() => {
    if (!snapshot || !deepLinkIncidentId || appliedDeepLinkIncidentId.current === deepLinkIncidentId) return;
    const linkedIncident = snapshot.incidents.find((incident) => incident.id === deepLinkIncidentId);
    if (!linkedIncident) return;
    appliedDeepLinkIncidentId.current = deepLinkIncidentId;
    const portfolioId = linkedIncident.environment === "all"
      ? "kpn-fiam"
      : environmentPortfolio.get(linkedIncident.environment) || "";
    if (portfolioId) {
      setSelectedPortfolioId(portfolioId);
    }
    setActiveView("incidents");
    setSelectedEnv("all");
    setSearch("");
    setSelectedIncident(linkedIncident);
    setSelectedService(undefined);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(incidentElementId(linkedIncident.id));
        target?.scrollIntoView({ block: "center", behavior: "smooth" });
        target?.focus({ preventScroll: true });
      });
    });
  }, [deepLinkIncidentId, environmentPortfolio, snapshot]);

  async function handleReport() {
    try {
      const result = await generateReport("monthly", reportMonth);
      setReport({ markdown: result.markdown, filename: result.filename });
    } catch (reportError) {
      setToast(reportError instanceof Error ? reportError.message : "Report generation failed");
    }
  }

  async function handleReportMonth(month: string) {
    setReportMonth(month);
    try {
      const result = await generateReport("monthly", month);
      setReport({ markdown: result.markdown, filename: result.filename });
    } catch (reportError) {
      setToast(reportError instanceof Error ? reportError.message : "Report generation failed");
    }
  }

  async function handleDownload() {
    try {
      const result = await downloadReport("monthly", reportMonth);
      setToast(`Downloaded ${result.filename}`);
    } catch (reportError) {
      setToast(reportError instanceof Error ? reportError.message : "Report download failed");
    }
  }

  async function handleDownloadMonth(month: string) {
    setReportMonth(month);
    try {
      const result = await downloadReport("monthly", month);
      setToast(`Downloaded ${result.filename}`);
    } catch (reportError) {
      setToast(reportError instanceof Error ? reportError.message : "Report download failed");
    }
  }

  async function handleEmailReport() {
    if (!session?.permissions.canRunActions) {
      setToast("Admin role required");
      return;
    }
    try {
      const result = await sendMonthlyReportEmail(reportMonth, "monthly");
      setToast(result.skipped ? `${result.reason}${result.missing?.length ? `: ${result.missing.join(", ")}` : ""}` : "Report email sent");
    } catch (reportError) {
      setToast(reportError instanceof Error ? reportError.message : "Report email failed");
    }
  }

  async function handleSlack(incident: Incident) {
    if (!snapshot) return;
    if (!session?.permissions.canRunActions) {
      setToast("Admin role required");
      return;
    }
    try {
      const result = await sendIncidentToSlack(incident.id, snapshot.channels.ops);
      setToast(result.skipped ? result.reason || "Slack notification skipped" : "Slack notification sent");
    } catch (slackError) {
      setToast(slackError instanceof Error ? slackError.message : "Slack notification failed");
    }
  }

  async function handleServiceReset(service: ServiceRow) {
    if (!session?.permissions.canRunActions) {
      setToast("Admin role required");
      return;
    }
    const supported = serviceResetSupport(service, true);
    if (!supported.supported) {
      setToast(supported.message);
      return;
    }
    const confirmed = window.confirm(
      [
        `Reset ${service.dshServiceName} in ${service.environmentLabel}?`,
        "",
        "This will ask the KPN DSH API to restart the service. Use it only after checking the incident context."
      ].join("\n")
    );
    if (!confirmed) return;

    setResetPendingId(service.id);
    try {
      const result = await resetService(service.environment, service.serviceId, `Manual reset by ${session.user?.username || "admin"} from Protium Monitoring`);
      setToast(`Reset requested for ${result.service.dshServiceName}`);
      await load(true, { silent: true });
    } catch (resetError) {
      setToast(resetError instanceof Error ? resetError.message : "Service reset failed");
    } finally {
      setResetPendingId(null);
    }
  }

  async function handleToggleSlackAlerts() {
    if (!snapshot) return;
    if (!session?.permissions.canRunActions) {
      setToast("Admin role required");
      return;
    }
    const enable = Boolean(snapshot.integrationHealth.slack.paused);
    try {
      await setSlackAlertsEnabled(enable);
      setToast(enable ? "Slack alerts resumed" : "Slack alerts paused");
      await load(true, { silent: true });
    } catch (slackError) {
      setToast(slackError instanceof Error ? slackError.message : "Slack alert setting failed");
    }
  }

  async function handleLogin(username: string, password: string) {
    const nextSession = await login(username, password);
    setSession(nextSession);
    setAuthRequired(false);
    setLoading(true);
    setError(null);
    try {
      const next = await fetchSnapshot(true);
      setSnapshot(next);
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskeyLogin(username: string) {
    const normalized = username.trim().toLowerCase();
    if (!normalized) {
      throw new Error("Username is required for passkey sign-in");
    }
    const { options } = await passkeyAuthenticationOptions(normalized);
    const credential = await getPasskeyAssertion(options);
    const nextSession = await verifyPasskeyAuthentication(credential);
    setSession(nextSession);
    setAuthRequired(false);
    setLoading(true);
    setError(null);
    try {
      const next = await fetchSnapshot(true);
      setSnapshot(next);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await logout();
    } finally {
      setSession(null);
      setSnapshot(null);
      setAuthRequired(true);
      setActiveView("command");
    }
  }

  if (authRequired && !session) {
    return <LoginPage ozoneSsoEnabled={authOptions.ozoneSsoEnabled} onLogin={handleLogin} onPasskeyLogin={handlePasskeyLogin} />;
  }

  if (error) {
    return (
      <main className="errorState">
        <Shield size={30} />
        <h1>Protium Monitoring</h1>
        <p>{error}</p>
        <button className="primaryButton" onClick={() => load(true)}>Retry</button>
      </main>
    );
  }

  if (!snapshot) {
    return (
      <main className="loadingState">
        <RefreshCw size={32} className="spin" />
        <span>Loading reliability snapshot</span>
      </main>
    );
  }

  function handleSelectService(service: ServiceRow) {
    setSelectedService(service);
    setSelectedIncident(undefined);
  }

  function handleSelectIncident(incident: Incident) {
    setSelectedIncident(incident);
    setSelectedService(undefined);
  }

  const selectedIncidentInScope = selectedIncident ? filteredIncidents.find((incident) => incident.id === selectedIncident.id) : undefined;
  const selectedServiceInScope = selectedService ? filteredServices.find((service) => service.id === selectedService.id) : undefined;
  const activeIncident = selectedIncidentInScope || filteredIncidents[0];
  const activeService =
    selectedServiceInScope ||
    filteredServices.find((service) => service.serviceId === activeIncident?.serviceId && service.environment === activeIncident?.environment) ||
    filteredServices[0];
  const portfolioEnvironments = snapshot.environments.filter((environment) => environment.portfolioId === activePortfolioId);
  const scopedEnvironments = portfolioEnvironments.filter((environment) => selectedEnv === "all" || environment.id === selectedEnv);
  const environmentOptions = portfolioEnvironments.length
    ? [{ id: "all", label: "All envs" }, ...portfolioEnvironments.map((environment) => ({ id: environment.id, label: environmentShortLabel(environment) }))]
    : defaultEnvOptions;
  const activePortfolio = snapshot.portfolios.find((portfolio) => portfolio.id === activePortfolioId);
  const activePortfolios = activePortfolio ? [activePortfolio] : [];
  const activeIntegrations = scopedIntegrationHealth(snapshot, scopedEnvironments, filteredServices);
  const activeIncidentIds = new Set(filteredIncidents.map((incident) => incident.id));
  const activeNotifications = snapshot.notifications.filter((notification) =>
    activeIncidentIds.size
      ? [...activeIncidentIds].some((incidentId) => String(notification.id || "").includes(incidentId) || String(notification.text || "").includes(incidentId))
      : false
  );
  const contractPortfolioId = snapshot.contract?.portfolioId || "kpn-fiam";
  const activeContract = activePortfolioId === contractPortfolioId ? snapshot.contract : null;
  const activeSnapshot: Snapshot = {
    ...snapshot,
    contract: activeContract,
    sourceCatalog: activeContract ? snapshot.sourceCatalog : [],
    portfolios: activePortfolios,
    environments: scopedEnvironments,
    services: filteredServices,
    endpointProbes: filteredEndpointProbes,
    certificates: filteredCertificates,
    incidents: filteredIncidents,
    notifications: activeNotifications,
    integrationHealth: activeIntegrations,
    reportChecklist: scopedReportChecklist(snapshot, activePortfolio, scopedEnvironments, filteredServices, filteredIncidents, filteredEndpointProbes, activeIntegrations),
    metrics: scopedMetrics(snapshot, activePortfolios, filteredServices, filteredIncidents, filteredEndpointProbes, activeIntegrations)
  };

  const commandView = (
    <>
      <MobileIssueOverview
        incidents={filteredIncidents}
        services={filteredServices}
        snapshot={activeSnapshot}
        activeId={activeIncident?.id}
        onSelectIncident={handleSelectIncident}
        onSelectService={handleSelectService}
        onOpenIncidents={() => setActiveView("incidents")}
      />
      <div className="topGrid">
        <IncidentQueue incidents={filteredIncidents} activeId={activeIncident?.id} onSelect={handleSelectIncident} />
        <UptimeStrip environments={scopedEnvironments} snapshot={activeSnapshot} />
      </div>
      <ServicesMatrix services={filteredServices} snapshot={activeSnapshot} selectedEnv={selectedEnv} selectedServiceId={activeService?.id} onSelect={handleSelectService} />
      <div className="bottomGrid">
        <SlackTimeline snapshot={activeSnapshot} canRunActions={Boolean(session?.permissions.canRunActions)} onSend={handleSlack} />
        <ReportReadiness
          snapshot={activeSnapshot}
          canRunActions={Boolean(session?.permissions.canRunActions)}
          onReport={handleReport}
          onDownload={handleDownload}
          onEmail={handleEmailReport}
          reportMonth={reportMonth}
        />
      </div>
    </>
  );

  const activeViewContent: Record<ViewKey, ReactNode> = {
    command: commandView,
    environments: (
      <EnvironmentView
        snapshot={activeSnapshot}
        environments={scopedEnvironments}
        services={filteredServices}
        onSelectService={handleSelectService}
        onOpenServices={() => setActiveView("services")}
      />
    ),
    services: <ServicesView snapshot={activeSnapshot} services={filteredServices} selectedEnv={selectedEnv} selectedServiceId={activeService?.id} onSelect={handleSelectService} />,
    incidents: (
      <IncidentsView
        incidents={filteredIncidents}
        activeId={activeIncident?.id}
        canRunActions={Boolean(session?.permissions.canRunActions)}
        slackPaused={activeSnapshot.integrationHealth.slack.paused}
        onSelect={handleSelectIncident}
        onSend={handleSlack}
      />
    ),
    logs: <LogsView snapshot={activeSnapshot} services={filteredServices} endpointProbes={filteredEndpointProbes} />,
    certificates: <CertificatesView certificates={filteredCertificates} services={filteredServices} onSelectService={handleSelectService} />,
    reports: (
      <ReportsView
        snapshot={activeSnapshot}
        canRunActions={Boolean(session?.permissions.canRunActions)}
        onReport={handleReport}
        onDownload={handleDownload}
        onReportMonth={handleReportMonth}
        onDownloadMonth={handleDownloadMonth}
        onEmail={handleEmailReport}
        reportMonth={reportMonth}
        setReportMonth={setReportMonth}
      />
    ),
    security: <SecurityView session={session} onToast={setToast} />,
    users: <UsersView session={session} onToast={setToast} />
  };

  return (
    <div className="appShell">
      <Sidebar
        activeView={activeView}
        criticalCount={activeSnapshot.metrics.criticalIncidents}
        openIncidentCount={activeSnapshot.metrics.openIncidents}
        session={session}
        onViewChange={setActiveView}
      />
      <main className="workspace">
        <Header
          portfolioOptions={portfolioOptions}
          selectedPortfolioId={activePortfolioId}
          setSelectedPortfolioId={handlePortfolioChange}
          environmentOptions={environmentOptions}
          selectedEnv={selectedEnv}
          session={session}
          setSelectedEnv={setSelectedEnv}
          search={search}
          setSearch={setSearch}
          onRefresh={() => load(true)}
          onReport={handleReport}
          onDownload={handleDownload}
          loading={loading}
          reportMonth={reportMonth}
          setReportMonth={setReportMonth}
          onLogout={handleLogout}
        />
        <SourceStrip
          snapshot={activeSnapshot}
          environments={scopedEnvironments}
          services={filteredServices}
          endpointProbes={filteredEndpointProbes}
          canRunActions={Boolean(session?.permissions.canRunActions)}
          onToggleSlackAlerts={handleToggleSlackAlerts}
        />
        <div className="contentGrid">
          <div className="mainColumn">
            {activeViewContent[activeView]}
          </div>
          <DetailDrawer
            service={activeService}
            incident={activeIncident}
            snapshot={activeSnapshot}
            canRunActions={Boolean(session?.permissions.canRunActions)}
            resetPendingId={resetPendingId}
            onSend={handleSlack}
            onResetService={handleServiceReset}
          />
        </div>
      </main>
      {toast && <button className="toast" onClick={() => setToast(null)}>{toast}</button>}
      {report && <ReportModal markdown={report.markdown} filename={report.filename} onClose={() => setReport(null)} onDownload={handleDownload} />}
    </div>
  );
}
